import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Settings, LogOut, Plus, Pencil, Trash2, Search, 
  Download, ChevronRight, ChevronDown, Building2, UserPlus, Shield, Eye,
  Trophy, Goal, Bike, Waves, Timer, Dumbbell, Medal,
  LayoutDashboard, FolderTree, UserCog, FileSpreadsheet,
  ArrowUpDown, Filter, MoreVertical, ImagePlus, Upload,
  Link2, CheckCircle2, XCircle, Clock, ExternalLink, Copy, Bell,
  DollarSign, Receipt, Percent, RefreshCw, FileText, Wallet,
  TrendingUp, TrendingDown, CreditCard, PiggyBank, BookOpen, Lock, AlertTriangle, ArrowLeft,
  UserCheck, UserX, LayoutGrid, List, Menu, X, ArrowUp, ArrowDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import FinanzasModule from '@/components/club/FinanzasModule';
import ContabilidadModule from '@/components/club/ContabilidadModule';
import MorosidadSection from '@/components/club/MorosidadSection';
import TermsAcceptanceModal from '@/components/TermsAcceptanceModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Función para formatear RUT chileno
const formatRut = (value) => {
  // Eliminar todo excepto números y K
  let rut = value.replace(/[^0-9kK]/g, '').toUpperCase();
  
  if (rut.length === 0) return '';
  
  // Separar cuerpo y dígito verificador
  let dv = rut.slice(-1);
  let cuerpo = rut.slice(0, -1);
  
  // Formatear cuerpo con puntos
  let cuerpoFormateado = '';
  let contador = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
    contador++;
    if (contador === 3 && i !== 0) {
      cuerpoFormateado = '.' + cuerpoFormateado;
      contador = 0;
    }
  }
  
  // Si solo hay dígitos sin DV aún, no agregar guión
  if (cuerpo.length === 0) return dv;
  
  return cuerpoFormateado + '-' + dv;
};

// ==================== VALIDACIONES DE DOCUMENTOS ====================

// Validar RUT Chileno (módulo 11)
const validarRutChileno = (rut) => {
  if (!rut) return { valido: false, mensaje: 'RUT requerido' };
  
  const rutLimpio = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rutLimpio.length < 8) return { valido: false, mensaje: 'RUT muy corto' };
  
  const cuerpo = rutLimpio.slice(0, -1);
  const dvIngresado = rutLimpio.slice(-1);
  
  let suma = 0;
  let multiplicador = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  const resto = suma % 11;
  const dvCalculado = resto === 0 ? '0' : resto === 1 ? 'K' : String(11 - resto);
  
  if (dvIngresado === dvCalculado) {
    return { valido: true, mensaje: 'RUT válido' };
  }
  return { valido: false, mensaje: `Dígito verificador incorrecto (debería ser ${dvCalculado})` };
};

// Validar CPF Brasileño
const validarCpfBrasileno = (cpf) => {
  if (!cpf) return { valido: false, mensaje: 'CPF requerido' };
  
  const cpfLimpio = cpf.replace(/[^0-9]/g, '');
  if (cpfLimpio.length !== 11) return { valido: false, mensaje: 'CPF debe tener 11 dígitos' };
  
  if (/^(\d)\1+$/.test(cpfLimpio)) return { valido: false, mensaje: 'CPF inválido' };
  
  let suma = 0;
  for (let i = 0; i < 9; i++) suma += parseInt(cpfLimpio[i]) * (10 - i);
  let resto = (suma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== parseInt(cpfLimpio[9])) return { valido: false, mensaje: 'CPF inválido' };
  
  suma = 0;
  for (let i = 0; i < 10; i++) suma += parseInt(cpfLimpio[i]) * (11 - i);
  resto = (suma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== parseInt(cpfLimpio[10])) return { valido: false, mensaje: 'CPF inválido' };
  
  return { valido: true, mensaje: 'CPF válido' };
};

// Validar DNI Español
const validarDniEspanol = (dni) => {
  if (!dni) return { valido: false, mensaje: 'DNI requerido' };
  
  const dniLimpio = dni.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (dniLimpio.length !== 9) return { valido: false, mensaje: 'DNI debe tener 8 dígitos y 1 letra' };
  
  const numero = dniLimpio.slice(0, 8);
  const letraIngresada = dniLimpio.slice(-1);
  
  if (!/^\d{8}$/.test(numero)) return { valido: false, mensaje: 'Formato incorrecto' };
  
  const letras = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const letraCalculada = letras[parseInt(numero) % 23];
  
  if (letraIngresada === letraCalculada) return { valido: true, mensaje: 'DNI válido' };
  return { valido: false, mensaje: `Letra incorrecta (debería ser ${letraCalculada})` };
};

// Validar CI Uruguaya
const validarCiUruguaya = (ci) => {
  if (!ci) return { valido: false, mensaje: 'CI requerida' };
  
  const ciLimpia = ci.replace(/[^0-9]/g, '');
  if (ciLimpia.length < 7 || ciLimpia.length > 8) return { valido: false, mensaje: 'CI debe tener 7 u 8 dígitos' };
  
  const ciPadded = ciLimpia.padStart(8, '0');
  const multiplicadores = [2, 9, 8, 7, 6, 3, 4];
  let suma = 0;
  for (let i = 0; i < 7; i++) suma += parseInt(ciPadded[i]) * multiplicadores[i];
  const resto = suma % 10;
  const dvCalculado = resto === 0 ? 0 : 10 - resto;
  
  if (parseInt(ciPadded[7]) === dvCalculado) return { valido: true, mensaje: 'CI válida' };
  return { valido: false, mensaje: 'Dígito verificador incorrecto' };
};

// Validar documento según país
const validarDocumento = (documento, pais, tipoDocumento) => {
  if (!documento) return { valido: false, mensaje: 'Documento requerido' };
  
  if (tipoDocumento === 'pasaporte') {
    if (documento.length >= 5) return { valido: true, mensaje: 'Pasaporte válido' };
    return { valido: false, mensaje: 'Pasaporte debe tener al menos 5 caracteres' };
  }
  
  switch (pais) {
    case 'CL': return validarRutChileno(documento);
    case 'BR': return validarCpfBrasileno(documento);
    case 'ES': return validarDniEspanol(documento);
    case 'UY': return validarCiUruguaya(documento);
    case 'AR':
      const dniAr = documento.replace(/[^0-9]/g, '');
      if (dniAr.length >= 7 && dniAr.length <= 8) return { valido: true, mensaje: 'DNI válido' };
      return { valido: false, mensaje: 'DNI debe tener 7 u 8 dígitos' };
    case 'PE':
      const dniPe = documento.replace(/[^0-9]/g, '');
      if (dniPe.length === 8) return { valido: true, mensaje: 'DNI válido' };
      return { valido: false, mensaje: 'DNI debe tener 8 dígitos' };
    case 'CO':
      const cc = documento.replace(/[^0-9]/g, '');
      if (cc.length >= 6 && cc.length <= 10) return { valido: true, mensaje: 'CC válida' };
      return { valido: false, mensaje: 'CC debe tener entre 6 y 10 dígitos' };
    default:
      if (documento.length >= 5) return { valido: true, mensaje: 'Documento válido' };
      return { valido: false, mensaje: 'Documento debe tener al menos 5 caracteres' };
  }
};

// Formatear CPF brasileño
const formatCpf = (value) => {
  const cpf = value.replace(/[^0-9]/g, '');
  if (cpf.length <= 3) return cpf;
  if (cpf.length <= 6) return `${cpf.slice(0, 3)}.${cpf.slice(3)}`;
  if (cpf.length <= 9) return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6)}`;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9, 11)}`;
};

// Formatear DNI español
const formatDniEspanol = (value) => {
  const dni = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (dni.length <= 8) return dni;
  return `${dni.slice(0, 8)}-${dni.slice(8, 9)}`;
};

// Mapeo de iconos de disciplinas
const DISCIPLINA_ICONS = {
  futbol: Goal,
  tenis: Trophy,
  basquetbol: Trophy,
  voleibol: Trophy,
  karate: Shield,
  natacion: Waves,
  atletismo: Timer,
  ciclismo: Bike,
  crossfit: Dumbbell,
  triathlon: Medal,
  otro: Trophy
};

// Configuración de países y documentos
const PAISES_CONFIG = [
  { id: "CL", nombre: "Chile", documento_principal: "RUT", formato: "##.###.###-X" },
  { id: "AR", nombre: "Argentina", documento_principal: "DNI", formato: "##.###.###" },
  { id: "BR", nombre: "Brasil", documento_principal: "CPF", formato: "###.###.###-##" },
  { id: "PE", nombre: "Perú", documento_principal: "DNI", formato: "########" },
  { id: "CO", nombre: "Colombia", documento_principal: "CC", formato: "#.###.###.###" },
  { id: "MX", nombre: "México", documento_principal: "CURP", formato: "texto" },
  { id: "EC", nombre: "Ecuador", documento_principal: "CI", formato: "##########" },
  { id: "UY", nombre: "Uruguay", documento_principal: "CI", formato: "#.###.###-#" },
  { id: "PY", nombre: "Paraguay", documento_principal: "CI", formato: "#.###.###" },
  { id: "BO", nombre: "Bolivia", documento_principal: "CI", formato: "#######" },
  { id: "VE", nombre: "Venezuela", documento_principal: "CI", formato: "V-##.###.###" },
  { id: "ES", nombre: "España", documento_principal: "DNI", formato: "########-X" },
  { id: "US", nombre: "Estados Unidos", documento_principal: "SSN", formato: "###-##-####" },
  { id: "OTHER", nombre: "Otro país", documento_principal: "ID", formato: "texto" },
];

// Función para formatear documento según país
const formatDocumento = (value, pais) => {
  switch (pais) {
    case 'CL': return formatRut(value);
    case 'BR': return formatCpf(value);
    case 'ES': return formatDniEspanol(value);
    default: return value.replace(/[^0-9a-zA-Z-]/g, '').toUpperCase();
  }
};

// Obtener nombre del documento según país
const getNombreDocumento = (pais) => {
  const config = PAISES_CONFIG.find(p => p.id === pais);
  return config ? config.documento_principal : 'ID';
};

export default function ClubDashboard({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [clubes, setClubes] = useState([]);
  const [clubActual, setClubActual] = useState(null);
  const [disciplinas, setDisciplinas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [integrantes, setIntegrantes] = useState([]);
  const [usuariosClub, setUsuariosClub] = useState([]);
  
  // Estados de límites del plan
  const [limitesPlan, setLimitesPlan] = useState({
    categoriasBloqueadas: 0,
    integrantesBloqueados: 0,
    totalCategorias: 0,
    totalIntegrantes: 0,
    tieneMembresia: false
  });
  
  // Estados de UI
  const [activeSection, setActiveSection] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [estadoFiltro, setEstadoFiltro] = useState('activo'); // activo, suspendido, todos
  const [ordenarPor, setOrdenarPor] = useState('apellido');
  const [ordenDir, setOrdenDir] = useState('asc');
  const [sidebarOpen, setSidebarOpen] = useState(false); // Cerrado por defecto en móvil
  
  // Función para cambiar sección y cerrar sidebar en móvil
  const handleSetSection = (section) => {
    setActiveSection(section);
    setSidebarOpen(false);
  };
  
  // Estados para filtro en vista de categoría
  const [searchTermCategoria, setSearchTermCategoria] = useState('');
  const [ordenarPorCategoria, setOrdenarPorCategoria] = useState('apellido');
  const [ordenDirCategoria, setOrdenDirCategoria] = useState('asc');
  
  // Estados de diálogos
  const [showClubDialog, setShowClubDialog] = useState(false);
  const [showCategoriaDialog, setShowCategoriaDialog] = useState(false);
  const [showIntegranteDialog, setShowIntegranteDialog] = useState(false);
  const [showInvitarDialog, setShowInvitarDialog] = useState(false);
  const [showAsociarDialog, setShowAsociarDialog] = useState(false);
  const [showImportarDialog, setShowImportarDialog] = useState(false);
  const [showLinkInscripcionDialog, setShowLinkInscripcionDialog] = useState(false);
  const [showCartolaDialog, setShowCartolaDialog] = useState(false);
  const [showSuspenderDialog, setShowSuspenderDialog] = useState(false);
  const [integranteASuspender, setIntegranteASuspender] = useState(null);
  const [suspenderForm, setSuspenderForm] = useState({ motivo: '', detalle: '' });
  const [importandoExcel, setImportandoExcel] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFields, setExportFields] = useState([
    { key: 'apellido', label: 'Apellido', checked: true },
    { key: 'nombre', label: 'Nombre', checked: true },
    { key: 'rut', label: 'RUT', checked: true },
    { key: 'fecha_nacimiento', label: 'Fecha Nacimiento', checked: true },
    { key: 'email', label: 'Email', checked: true },
    { key: 'telefono', label: 'Teléfono', checked: true },
    { key: 'categorias', label: 'Categorías', checked: true },
    { key: 'estado', label: 'Estado', checked: true },
    { key: 'fecha_ingreso', label: 'Fecha Ingreso', checked: false },
    { key: 'direccion', label: 'Dirección', checked: false },
    { key: 'observaciones', label: 'Observaciones', checked: false },
  ]);
  const [editingItem, setEditingItem] = useState(null);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [inscripcionesPendientes, setInscripcionesPendientes] = useState([]);
  const [linkInscripcion, setLinkInscripcion] = useState(null);
  const [cartolaIntegrante, setCartolaIntegrante] = useState(null);
  const [cartolaData, setCartolaData] = useState(null);
  const [loadingCartola, setLoadingCartola] = useState(false);
  const [cartolaPlanes, setCartolaPlanes] = useState([]);
  const [cartolaPagos, setCartolaPagos] = useState([]);
  const [cartolaFiltro, setCartolaFiltro] = useState('todos');
  const [cartolaTab, setCartolaTab] = useState('cuotas');
  const [searchCategoriaClub, setSearchCategoriaClub] = useState('');
  
  // Menú colapsable states
  const [menuPlanesOpen, setMenuPlanesOpen] = useState(false);
  const [menuPagosOpen, setMenuPagosOpen] = useState(false);
  const [menuContabilidadOpen, setMenuContabilidadOpen] = useState(false);
  const [menuFinanzasOpen, setMenuFinanzasOpen] = useState(false);
  
  // Vista de lista o tarjetas (guardada en localStorage)
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('kinosis_club_view_mode') || 'cards';
  });
  
  // Guardar preferencia de vista en localStorage
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('kinosis_club_view_mode', mode);
  };
  
  // Modal de términos
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // Enrollment photos cache
  const [enrollmentData, setEnrollmentData] = useState({});
  
  // Formularios
  const [clubForm, setClubForm] = useState({ nombre: '', disciplina: 'futbol', pais: 'CL', descripcion: '', direccion: '', telefono: '', email: '', logo: '', texto_bienvenida: '' });
  const [categoriaForm, setCategoriaForm] = useState({ nombre: '', descripcion: '', edad_minima: '', edad_maxima: '' });
  const [integranteForm, setIntegranteForm] = useState({ nombre: '', apellido: '', tipo_documento: 'documento', rut: '', fecha_nacimiento: '', email: '', telefono: '', direccion: '', categoria_ids: [], fecha_ingreso: '', observaciones: '', foto: '' });
  const [invitarForm, setInvitarForm] = useState({ email: '', rol: 'lectura' });
  
  // Estados para datos bancarios
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [showDatosBancariosDialog, setShowDatosBancariosDialog] = useState(false);
  const [datosBancariosForm, setDatosBancariosForm] = useState({
    banco: '',
    tipo_cuenta: 'corriente',
    numero_cuenta: '',
    rut_titular: '',
    nombre_titular: '',
    email_notificacion: ''
  });
  
  // Estados para transacciones de pago
  const [showPagoTransferenciaDialog, setShowPagoTransferenciaDialog] = useState(false);
  const [transaccionActiva, setTransaccionActiva] = useState(null);
  const [loadingTransaccion, setLoadingTransaccion] = useState(false);
  
  // Modal Upgrade a Pro
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeForm, setUpgradeForm] = useState({
    nombre: '', email: '', telefono: '', mensaje: ''
  });
  const [enviandoUpgrade, setEnviandoUpgrade] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Verificar si el usuario necesita aceptar términos
  useEffect(() => {
    if (user && user.acepto_terminos === false) {
      setShowTermsModal(true);
    }
  }, [user]);

  const handleTermsAccepted = async () => {
    setShowTermsModal(false);
    // Recargar datos del usuario
    try {
      const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      // El usuario se actualizará en el componente padre
    } catch (e) {}
  };

  const loadInitialData = async () => {
    try {
      const [disciplinasRes, clubesRes] = await Promise.all([
        axios.get(`${API}/disciplinas-deportivas`),
        axios.get(`${API}/clubes-deportivos`, { withCredentials: true })
      ]);
      
      setDisciplinas(disciplinasRes.data);
      
      // Ordenar clubes: admin primero, luego super_usuario, luego lectura
      const clubesOrdenados = clubesRes.data.sort((a, b) => {
        const prioridad = { admin: 0, super_usuario: 1, lectura: 2 };
        return (prioridad[a.mi_rol] || 2) - (prioridad[b.mi_rol] || 2);
      });
      
      setClubes(clubesOrdenados);
      
      if (clubesOrdenados.length > 0) {
        setClubActual(clubesOrdenados[0]);
        await loadClubData(clubesOrdenados[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarClub = async (club) => {
    setClubActual(club);
    setActiveSection('dashboard');
    await loadClubData(club.id);
  };

  const loadClubData = async (clubId) => {
    try {
      const [categoriasRes, integrantesRes, usuariosRes, pendientesRes, datosBancariosRes] = await Promise.all([
        axios.get(`${API}/clubes-deportivos/${clubId}/categorias`, { withCredentials: true }),
        axios.get(`${API}/clubes-deportivos/${clubId}/integrantes`, { withCredentials: true }),
        axios.get(`${API}/clubes-deportivos/${clubId}/usuarios`, { withCredentials: true }),
        axios.get(`${API}/clubes-deportivos/${clubId}/inscripciones-pendientes`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/clubes-deportivos/${clubId}/datos-bancarios`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      // Manejar nueva estructura de respuesta con información de bloqueos
      const categoriasData = categoriasRes.data.categorias || categoriasRes.data;
      const integrantesData = integrantesRes.data.integrantes || integrantesRes.data;
      
      setCategorias(categoriasData);
      setIntegrantes(integrantesData);
      setUsuariosClub(usuariosRes.data);
      setInscripcionesPendientes(pendientesRes.data);
      setDatosBancarios(datosBancariosRes.data);
      
      // Actualizar información de límites del plan
      setLimitesPlan({
        categoriasBloqueadas: categoriasRes.data.bloqueadas || 0,
        integrantesBloqueados: integrantesRes.data.bloqueados || 0,
        totalCategorias: categoriasRes.data.total || categoriasData.length,
        totalIntegrantes: integrantesRes.data.total || integrantesData.length,
        tieneMembresia: categoriasRes.data.tiene_membresia || false
      });
      
      // Load enrollment photos for integrantes with RUTs
      const ruts = integrantesData
        .map(i => i.rut)
        .filter(r => r && r.length > 0);
      if (ruts.length > 0) {
        try {
          const enrollRes = await axios.post(`${API}/enrolamiento/batch-consulta`, { ruts });
          setEnrollmentData(enrollRes.data.resultados || {});
        } catch (e) {
          // Non-critical, silently fail
        }
      }
    } catch (error) {
      console.error('Error loading club data:', error);
    }
  };

  // Función de refresh con animación
  const handleRefresh = async () => {
    if (!clubActual || refreshing) return;
    setRefreshing(true);
    await loadClubData(clubActual.id);
    setRefreshing(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem('session_token');
    navigate('/');
  };

  // CRUD Club
  const handleSaveClub = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/clubes-deportivos/${editingItem.id}`, clubForm, { withCredentials: true });
        toast.success('Club actualizado');
      } else {
        const res = await axios.post(`${API}/clubes-deportivos`, clubForm, { withCredentials: true });
        setClubes([...clubes, res.data]);
        setClubActual(res.data);
        toast.success('Club creado');
      }
      setShowClubDialog(false);
      setEditingItem(null);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  // CRUD Categoría
  const handleSaveCategoria = async () => {
    try {
      const data = {
        ...categoriaForm,
        edad_minima: categoriaForm.edad_minima ? parseInt(categoriaForm.edad_minima) : null,
        edad_maxima: categoriaForm.edad_maxima ? parseInt(categoriaForm.edad_maxima) : null
      };
      
      if (editingItem) {
        await axios.put(`${API}/clubes-deportivos/${clubActual.id}/categorias/${editingItem.id}`, data, { withCredentials: true });
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/clubes-deportivos/${clubActual.id}/categorias`, data, { withCredentials: true });
        toast.success('Categoría creada');
      }
      setShowCategoriaDialog(false);
      setEditingItem(null);
      setCategoriaForm({ nombre: '', descripcion: '', edad_minima: '', edad_maxima: '' });
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteCategoria = async (id) => {
    if (!confirm('¿Eliminar esta categoría?')) return;
    try {
      await axios.delete(`${API}/clubes-deportivos/${clubActual.id}/categorias/${id}`, { withCredentials: true });
      toast.success('Categoría eliminada');
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // CRUD Integrante
  const handleSaveIntegrante = async () => {
    try {
      // Use enrollment DOB if available
      const rutNorm = integranteForm.rut?.replace(/\./g, '').replace(/-/g, '').trim();
      const enroll = enrollmentData[rutNorm];
      const formToSave = { ...integranteForm };
      if (enroll?.fecha_nacimiento) {
        formToSave.fecha_nacimiento = enroll.fecha_nacimiento;
      }
      
      if (editingItem) {
        await axios.put(`${API}/clubes-deportivos/${clubActual.id}/integrantes/${editingItem.id}`, formToSave, { withCredentials: true });
        toast.success('Integrante actualizado');
      } else {
        await axios.post(`${API}/clubes-deportivos/${clubActual.id}/integrantes`, formToSave, { withCredentials: true });
        toast.success('Integrante creado');
      }
      setShowIntegranteDialog(false);
      setEditingItem(null);
      setIntegranteForm({ nombre: '', apellido: '', rut: '', fecha_nacimiento: '', email: '', telefono: '', direccion: '', categoria_ids: [], fecha_ingreso: '', observaciones: '', foto: '' });
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteIntegrante = async (id) => {
    if (!confirm('¿Eliminar este integrante?')) return;
    try {
      await axios.delete(`${API}/clubes-deportivos/${clubActual.id}/integrantes/${id}`, { withCredentials: true });
      toast.success('Integrante eliminado');
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Suspender integrante
  const handleSuspenderIntegrante = async () => {
    if (!integranteASuspender || !suspenderForm.motivo) {
      toast.error('Selecciona un motivo');
      return;
    }
    try {
      await axios.put(
        `${API}/clubes-deportivos/${clubActual.id}/integrantes/${integranteASuspender.id}/suspender`,
        suspenderForm,
        { withCredentials: true }
      );
      toast.success('Integrante suspendido');
      setShowSuspenderDialog(false);
      setIntegranteASuspender(null);
      setSuspenderForm({ motivo: '', detalle: '' });
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al suspender');
    }
  };

  // Reactivar integrante
  const handleReactivarIntegrante = async (integrante) => {
    if (!confirm(`¿Reactivar a ${integrante.nombre} ${integrante.apellido}?`)) return;
    try {
      await axios.put(
        `${API}/clubes-deportivos/${clubActual.id}/integrantes/${integrante.id}/reactivar`,
        {},
        { withCredentials: true }
      );
      toast.success('Integrante reactivado');
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reactivar');
    }
  };

  // Asociar/desasociar integrante de categoría
  const handleToggleIntegranteCategoria = async (integrante, categoriaId) => {
    const categoriaIds = integrante.categoria_ids || [];
    const isInCategoria = categoriaIds.includes(categoriaId);
    
    const newCategoriaIds = isInCategoria
      ? categoriaIds.filter(id => id !== categoriaId)
      : [...categoriaIds, categoriaId];
    
    try {
      await axios.put(
        `${API}/clubes-deportivos/${clubActual.id}/integrantes/${integrante.id}`,
        { ...integrante, categoria_ids: newCategoriaIds },
        { withCredentials: true }
      );
      toast.success(isInCategoria ? 'Integrante removido de la categoría' : 'Integrante agregado a la categoría');
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  // Obtener integrantes de una categoría (con filtro y orden)
  const getIntegrantesDeCategoria = (categoriaId) => {
    return integrantes
      .filter(i => i.categoria_ids?.includes(categoriaId))
      .filter(i => {
        if (!searchTermCategoria) return true;
        const searchLower = searchTermCategoria.toLowerCase();
        return `${i.nombre} ${i.apellido}`.toLowerCase().includes(searchLower) ||
               (i.rut && i.rut.toLowerCase().includes(searchLower));
      })
      .sort((a, b) => {
        const valA = a[ordenarPorCategoria] || '';
        const valB = b[ordenarPorCategoria] || '';
        return ordenDirCategoria === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      });
  };

  // Obtener integrantes que NO están en una categoría
  const getIntegrantesSinCategoria = (categoriaId) => {
    return integrantes.filter(i => !i.categoria_ids?.includes(categoriaId));
  };

  // Invitar usuario
  const handleInvitarUsuario = async () => {
    try {
      const res = await axios.post(`${API}/clubes-deportivos/${clubActual.id}/invitar-usuario`, invitarForm, { withCredentials: true });
      if (res.data.email_sent) {
        toast.success(`Invitación enviada a ${invitarForm.email}`);
      } else {
        toast.warning('Invitación creada pero el correo no pudo enviarse');
        const copied = await copyToClipboard(res.data.invite_url);
        if (copied) {
          toast.info('Link copiado al portapapeles como respaldo');
        } else {
          // Mostrar el link para que lo copie manualmente
          prompt('Copia este link de invitación:', res.data.invite_url);
        }
      }
      setShowInvitarDialog(false);
      setInvitarForm({ email: '', rol: 'lectura' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al invitar');
    }
  };

  // Exportar a Excel con campos seleccionados
  const handleExportarExcel = async () => {
    try {
      const selectedFields = exportFields.filter(f => f.checked).map(f => f.key);
      if (selectedFields.length === 0) { toast.error('Selecciona al menos un campo'); return; }
      const params = new URLSearchParams();
      if (categoriaFiltro !== 'todas') params.set('categoria_id', categoriaFiltro);
      params.set('campos', selectedFields.join(','));
      window.open(`${API}/clubes-deportivos/${clubActual.id}/integrantes/exportar?${params.toString()}`, '_blank');
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  // Mover campo en la lista de exportación
  const moveExportField = (index, direction) => {
    const newFields = [...exportFields];
    const target = index + direction;
    if (target < 0 || target >= newFields.length) return;
    [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
    setExportFields(newFields);
  };

  // Importar desde Excel
  const handleImportarExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!categoriaSeleccionada) {
      toast.error('Debes seleccionar una categoría primero');
      return;
    }
    
    setImportandoExcel(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${API}/clubes-deportivos/${clubActual.id}/categorias/${categoriaSeleccionada.id}/importar-integrantes`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      const { resultados } = res.data;
      
      if (resultados.errores.length > 0) {
        toast.warning(`${resultados.creados} creados, ${resultados.asociados} asociados. ${resultados.errores.length} errores.`);
        console.log('Errores de importación:', resultados.errores);
      } else {
        toast.success(`Importación exitosa: ${resultados.creados} creados, ${resultados.asociados} asociados`);
      }
      
      await loadClubData(clubActual.id);
      setShowImportarDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
    } finally {
      setImportandoExcel(false);
      e.target.value = ''; // Reset file input
    }
  };

  // Generar link de inscripción para categoría
  const handleGenerarLinkInscripcion = async () => {
    if (!categoriaSeleccionada) {
      toast.error('Selecciona una categoría primero');
      return;
    }
    if (!clubActual) {
      toast.error('No hay club seleccionado');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/clubes-deportivos/${clubActual.id}/categorias/${categoriaSeleccionada.id}/link-inscripcion`,
        {},
        { withCredentials: true }
      );
      setLinkInscripcion(res.data.url);
      setShowLinkInscripcionDialog(true);
    } catch (error) {
      console.error('Error generando link:', error);
      toast.error(error.response?.data?.detail || 'Error al generar el link');
    }
  };

  // Copiar link al portapapeles
  // Función auxiliar para copiar al portapapeles con fallback
  const copyToClipboard = async (text) => {
    // Método 1: API moderna de Clipboard (preferida)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('Clipboard API failed:', err);
      }
    }
    
    // Método 2: Fallback con textarea y execCommand
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        return true;
      }
    } catch (err) {
      console.warn('execCommand fallback failed:', err);
    }
    
    return false;
  };

  // Estado para mostrar modal con link
  const [linkParaCopiar, setLinkParaCopiar] = useState(null);

  const handleCopyLink = async (url) => {
    const success = await copyToClipboard(url);
    if (success) {
      toast.success('Link copiado al portapapeles');
    } else {
      // Mostrar modal con el link para copiar manualmente
      setLinkParaCopiar(url);
    }
  };

  // Obtener link de consulta
  const handleGetLinkConsulta = async () => {
    try {
      const res = await axios.get(`${API}/clubes-deportivos/${clubActual.id}/link-consulta`, { withCredentials: true });
      const success = await copyToClipboard(res.data.url);
      if (success) {
        toast.success('Link de consulta copiado al portapapeles');
      } else {
        // Mostrar modal con el link para copiar manualmente
        setLinkParaCopiar(res.data.url);
      }
    } catch (error) {
      toast.error('Error al obtener el link');
    }
  };

  // Guardar datos bancarios del club
  const handleSaveDatosBancarios = async () => {
    try {
      await axios.post(`${API}/clubes-deportivos/${clubActual.id}/datos-bancarios`, datosBancariosForm, { withCredentials: true });
      toast.success('Datos bancarios guardados');
      setShowDatosBancariosDialog(false);
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar datos bancarios');
    }
  };

  // Crear transacción de pago para transferencia
  const handleCrearTransaccionPago = async (integranteId, cuotaIds) => {
    setLoadingTransaccion(true);
    try {
      const res = await axios.post(`${API}/clubes-deportivos/${clubActual.id}/transacciones-pago`, {
        integrante_id: integranteId,
        cuota_ids: cuotaIds
      }, { withCredentials: true });
      setTransaccionActiva(res.data);
      setShowPagoTransferenciaDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear transacción');
    } finally {
      setLoadingTransaccion(false);
    }
  };

  // Aprobar inscripción
  const handleAprobarInscripcion = async (integranteId) => {
    try {
      await axios.put(`${API}/clubes-deportivos/${clubActual.id}/integrantes/${integranteId}/aprobar`, {}, { withCredentials: true });
      toast.success('Inscripción aprobada');
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error('Error al aprobar');
    }
  };

  // Rechazar inscripción
  const handleRechazarInscripcion = async (integranteId) => {
    if (!confirm('¿Rechazar esta solicitud de inscripción?')) return;
    try {
      await axios.put(`${API}/clubes-deportivos/${clubActual.id}/integrantes/${integranteId}/rechazar`, {}, { withCredentials: true });
      toast.success('Inscripción rechazada');
      loadClubData(clubActual.id);
    } catch (error) {
      toast.error('Error al rechazar');
    }
  };

  // Ver cartola del integrante
  const handleVerCartola = async (integrante) => {
    setCartolaIntegrante(integrante);
    setShowCartolaDialog(true);
    setLoadingCartola(true);
    setCartolaFiltro('todos');
    setCartolaTab('cuotas');
    try {
      const [cuotasRes, planesRes, pagosRes] = await Promise.all([
        axios.get(`${API}/clubes-deportivos/${clubActual.id}/integrantes/${integrante.id}/cuotas`, { withCredentials: true }),
        axios.get(`${API}/clubes-deportivos/${clubActual.id}/integrantes/${integrante.id}/planes`, { withCredentials: true }),
        axios.get(`${API}/clubes-deportivos/${clubActual.id}/pagos?integrante_id=${integrante.id}`, { withCredentials: true })
      ]);
      setCartolaData(cuotasRes.data);
      setCartolaPlanes(planesRes.data);
      setCartolaPagos(pagosRes.data);
    } catch (error) {
      console.error('Error loading cartola:', error);
      toast.error('Error al cargar la cartola');
    } finally {
      setLoadingCartola(false);
    }
  };

  // Recalcular cuotas pendientes (aplicar excepciones)
  const handleRecalcularCuotas = async () => {
    if (!confirm('¿Recalcular las cuotas pendientes aplicando las excepciones activas?')) return;
    try {
      const res = await axios.post(`${API}/clubes-deportivos/${clubActual.id}/integrantes/${cartolaIntegrante.id}/recalcular-cuotas`, {}, { withCredentials: true });
      toast.success(res.data.message);
      handleVerCartola(cartolaIntegrante); // Recargar cartola
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recalcular');
    }
  };

  // Desasignar plan de integrante
  const handleDesasignarPlan = async (planId) => {
    if (!confirm('¿Desasignar este plan? Las cuotas sin pago serán eliminadas.')) return;
    try {
      const res = await axios.delete(`${API}/clubes-deportivos/${clubActual.id}/integrantes/${cartolaIntegrante.id}/plan/${planId}`, { withCredentials: true });
      toast.success(res.data.message);
      handleVerCartola(cartolaIntegrante); // Recargar cartola
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desasignar plan');
    }
  };

  // Desactivar excepción/descuento
  const handleDesactivarExcepcion = async (excepcionId) => {
    if (!confirm('¿Desactivar esta excepción? Las cuotas pendientes se recalcularán.')) return;
    try {
      const res = await axios.delete(`${API}/clubes-deportivos/${clubActual.id}/excepciones-pago/${excepcionId}`, { withCredentials: true });
      toast.success(`${res.data.message}. ${res.data.cuotas_recalculadas} cuotas recalculadas.`);
      handleVerCartola(cartolaIntegrante); // Recargar cartola
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desactivar excepción');
    }
  };

  // Anular pago
  const handleAnularPago = async (pagoId) => {
    if (!confirm('¿Anular este pago? Se revertirá el estado de las cuotas afectadas.')) return;
    try {
      const res = await axios.delete(`${API}/clubes-deportivos/${clubActual.id}/pagos/${pagoId}`, { withCredentials: true });
      toast.success(res.data.message);
      handleVerCartola(cartolaIntegrante); // Recargar cartola
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al anular pago');
    }
  };

  // Filtrar cuotas por plan o tipo
  const cuotasFiltradas = cartolaData?.cuotas?.filter(c => {
    if (cartolaFiltro === 'todos') return true;
    if (cartolaFiltro === 'otros') return c.tipo === 'cargo_excepcional' || !c.plan_id;
    return c.plan_id === cartolaFiltro;
  }) || [];

  // ==================== UPGRADE A PRO ====================
  const handleOpenUpgrade = () => {
    setUpgradeForm({
      nombre: user?.name || '',
      email: user?.email || '',
      telefono: '',
      mensaje: ''
    });
    setShowUpgradeModal(true);
  };

  const handleEnviarUpgrade = async () => {
    if (!upgradeForm.nombre || !upgradeForm.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    setEnviandoUpgrade(true);
    try {
      await axios.post(`${API}/contacto/upgrade`, {
        nombre: upgradeForm.nombre,
        email: upgradeForm.email,
        telefono: upgradeForm.telefono,
        mensaje: upgradeForm.mensaje,
        organizacion_id: clubActual?.id,
        organizacion_nombre: clubActual?.nombre,
        organizacion_tipo: 'club',
        tipo: 'upgrade'
      });
      toast.success('¡Solicitud enviada! Te contactaremos pronto.');
      setShowUpgradeModal(false);
    } catch (error) {
      toast.error('Error al enviar la solicitud');
    } finally {
      setEnviandoUpgrade(false);
    }
  };

  // Formatear dinero
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  // Manejar logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setClubForm({ ...clubForm, logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Filtrar integrantes
  const integrantesFiltrados = integrantes
    .filter(i => {
      const matchSearch = searchTerm === '' || 
        `${i.nombre} ${i.apellido}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.rut && i.rut.includes(searchTerm));
      const matchCategoria = categoriaFiltro === 'todas' || 
        (categoriaFiltro === 'sin_categoria' ? (!i.categoria_ids || i.categoria_ids.length === 0) : (i.categoria_ids && i.categoria_ids.includes(categoriaFiltro)));
      const matchEstado = estadoFiltro === 'todos' || (i.estado || 'activo') === estadoFiltro;
      return matchSearch && matchCategoria && matchEstado;
    })
    .sort((a, b) => {
      const valA = a[ordenarPor] || '';
      const valB = b[ordenarPor] || '';
      return ordenDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });

  // Contadores de estado
  const integrantesActivos = integrantes.filter(i => (i.estado || 'activo') === 'activo').length;
  const integrantesSuspendidos = integrantes.filter(i => i.estado === 'suspendido').length;

  const canEdit = clubActual?.mi_rol !== 'lectura';
  const isAdmin = clubActual?.mi_rol === 'admin';
  const DisciplinaIcon = clubActual ? (DISCIPLINA_ICONS[clubActual.disciplina] || Trophy) : Trophy;

  // Helper: get enrollment photo for an integrante by their RUT
  const getEnrollmentPhoto = (rut) => {
    if (!rut) return null;
    const rutNorm = rut.replace(/[^0-9kK]/g, '').toUpperCase();
    return enrollmentData[rutNorm] || null;
  };

  // Avatar component with enrollment photo support
  const IntegranteAvatar = ({ integrante, size = 'md' }) => {
    const enrollment = getEnrollmentPhoto(integrante.rut);
    const sizeClasses = size === 'lg' ? 'w-12 h-12' : 'w-10 h-10';
    const textSize = size === 'lg' ? 'text-sm' : 'text-xs';
    
    if (enrollment?.foto_cara) {
      return (
        <div className={`${sizeClasses} rounded-full overflow-hidden shrink-0 relative`}>
          <img src={enrollment.foto_cara} alt="" className="w-full h-full object-cover" />
          {enrollment.verificado && (
            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full border-2 border-slate-800 flex items-center justify-center">
              <CheckCircle2 className="w-2.5 h-2.5 text-white" />
            </div>
          )}
        </div>
      );
    }
    
    if (integrante.foto) {
      return (
        <div className={`${sizeClasses} rounded-full overflow-hidden shrink-0`}>
          <img src={integrante.foto} alt="" className="w-full h-full object-cover" />
        </div>
      );
    }
    
    return (
      <div className={`${sizeClasses} rounded-full bg-blue-600 flex items-center justify-center text-white font-bold shrink-0 ${textSize}`}>
        {integrante.nombre?.charAt(0)}{integrante.apellido?.charAt(0)}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Vista sin club
  if (!clubActual) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full bg-slate-800/90 border-slate-700 text-center">
          <Building2 className="w-16 h-16 text-blue-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Bienvenido al Módulo de Clubes Deportivos</h2>
          <p className="text-slate-400 mb-6">No tienes ningún club registrado. Crea tu primer club para comenzar a gestionar integrantes, categorías y más.</p>
          <Button 
            onClick={() => { setEditingItem(null); setClubForm({ nombre: '', disciplina: 'futbol', descripcion: '', direccion: '', telefono: '', email: '', logo: '' }); setShowClubDialog(true); }}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" /> Crear Mi Club
          </Button>
        </Card>
        {/* Dialog Crear Club */}
        <Dialog open={showClubDialog} onOpenChange={setShowClubDialog}>
          <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Crear Club Deportivo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre del Club *</Label>
                <Input value={clubForm.nombre} onChange={(e) => setClubForm({ ...clubForm, nombre: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Disciplina Deportiva *</Label>
                <Select value={clubForm.disciplina} onValueChange={(v) => setClubForm({ ...clubForm, disciplina: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {disciplinas.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Logo del Club</Label>
                <div className="flex items-center gap-4">
                  {clubForm.logo && <img src={clubForm.logo} alt="Logo" className="w-16 h-16 rounded-lg object-cover" />}
                  <label className="flex items-center gap-2 px-4 py-2 bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-600">
                    <Upload className="w-4 h-4" />
                    <span>Subir imagen</span>
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Teléfono</Label>
                  <Input value={clubForm.telefono} onChange={(e) => setClubForm({ ...clubForm, telefono: e.target.value })} className="bg-slate-700 border-slate-600" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={clubForm.email} onChange={(e) => setClubForm({ ...clubForm, email: e.target.value })} className="bg-slate-700 border-slate-600" />
                </div>
              </div>
              <div>
                <Label>Dirección</Label>
                <Input value={clubForm.direccion} onChange={(e) => setClubForm({ ...clubForm, direccion: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
              <Button onClick={handleSaveClub} className="w-full bg-blue-600 hover:bg-blue-700">Crear Club</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Top Bar */}
      <header className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4 shrink-0">
        {/* Logo */}
        <div className="flex items-center gap-3">
          {/* Botón hamburguesa - visible solo en móvil */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <img src="/kinosis-logo.png" alt="Kinosis" className="h-8 sm:h-10 w-auto" />
        </div>
        
        {/* User Info & Logout */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
              {user.name?.charAt(0) || user.email?.charAt(0)}
            </div>
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-white">{user.name || user.email}</p>
              <p className="text-xs text-slate-400">{user.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-slate-700">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Cerrar Sesión</span>
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        {/* Overlay para cerrar el menú en móvil */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-20"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <aside className={`
          fixed lg:relative inset-y-0 left-0 z-30
          w-64 bg-slate-800 border-r border-slate-700 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:transform-none lg:shrink-0
          pt-16 lg:pt-0
        `}>
        {/* Club Selector */}
        {clubes.length > 1 && (
          <div className="p-3 border-b border-slate-700">
            <Select value={clubActual.id} onValueChange={(id) => handleCambiarClub(clubes.find(c => c.id === id))}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <div className="flex items-center gap-2 truncate">
                  <Building2 className="w-4 h-4 text-blue-400 shrink-0" />
                  <span className="truncate">{clubActual.nombre}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {clubes.map(club => {
                  const ClubIcon = DISCIPLINA_ICONS[club.disciplina] || Trophy;
                  return (
                    <SelectItem key={club.id} value={club.id}>
                      <div className="flex items-center gap-2">
                        <ClubIcon className="w-4 h-4 text-blue-400" />
                        <span>{club.nombre}</span>
                        <Badge className={`ml-2 text-xs ${club.mi_rol === 'admin' ? 'bg-amber-600' : club.mi_rol === 'super_usuario' ? 'bg-blue-600' : 'bg-slate-600'}`}>
                          {club.mi_rol === 'admin' ? 'Admin' : club.mi_rol === 'super_usuario' ? 'Super' : 'Lectura'}
                        </Badge>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Club Header */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            {clubActual.logo ? (
              <img src={clubActual.logo} alt="Logo" className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
                <DisciplinaIcon className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-white truncate">{clubActual.nombre}</h2>
              <p className="text-xs text-blue-400">{disciplinas.find(d => d.id === clubActual.disciplina)?.nombre || 'Deportivo'}</p>
            </div>
          </div>
          {clubActual.mi_rol && (
            <Badge className={`mt-2 ${clubActual.mi_rol === 'admin' ? 'bg-amber-600' : clubActual.mi_rol === 'super_usuario' ? 'bg-blue-600' : 'bg-slate-600'}`}>
              {clubActual.mi_rol === 'admin' ? 'Administrador' : clubActual.mi_rol === 'super_usuario' ? 'Super Usuario' : 'Solo Lectura'}
            </Badge>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <button
            onClick={() => handleSetSection('dashboard')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'dashboard' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Inicio</span>
          </button>
          <button
            onClick={() => handleSetSection('integrantes')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'integrantes' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
          >
            <Users className="w-5 h-5" />
            <span>Integrantes</span>
            <span className="ml-auto text-xs bg-slate-700 px-2 py-0.5 rounded-full">{integrantes.length}</span>
          </button>
          <button
            onClick={() => handleSetSection('categorias')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'categorias' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
          >
            <FolderTree className="w-5 h-5" />
            <span>Categorías</span>
            <span className="ml-auto text-xs bg-slate-700 px-2 py-0.5 rounded-full">{categorias.length}</span>
          </button>
          
          {/* Planes Menu */}
          {canEdit && (
            <>
              <button
                onClick={() => setMenuPlanesOpen(!menuPlanesOpen)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection.startsWith('planes-') ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
              >
                <FileText className="w-5 h-5" />
                <span>Planes</span>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${menuPlanesOpen ? 'rotate-180' : ''}`} />
              </button>
              {menuPlanesOpen && (
                <div className="ml-4 pl-4 border-l border-slate-600 space-y-1">
                  <button
                    onClick={() => handleSetSection('planes-cuotas')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'planes-cuotas' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>Planes de Cuotas</span>
                  </button>
                  <button
                    onClick={() => handleSetSection('planes-asignar-individual')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'planes-asignar-individual' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Asignar Individual</span>
                  </button>
                  <button
                    onClick={() => handleSetSection('planes-asignar-masivo')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'planes-asignar-masivo' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <Users className="w-4 h-4" />
                    <span>Asignar Masivo</span>
                  </button>
                </div>
              )}
            </>
          )}
          
          {/* Pagos Menu */}
          {canEdit && (
            <>
              <button
                onClick={() => setMenuPagosOpen(!menuPagosOpen)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection.startsWith('pagos-') ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
              >
                <Wallet className="w-5 h-5" />
                <span>Pagos</span>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${menuPagosOpen ? 'rotate-180' : ''}`} />
              </button>
              {menuPagosOpen && (
                <div className="ml-4 pl-4 border-l border-slate-600 space-y-1">
                  <button
                    onClick={() => handleSetSection('pagos-ingresos')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'pagos-ingresos' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <Plus className="w-4 h-4" />
                    <span>Registrar Ingreso</span>
                  </button>
                  <button
                    onClick={() => handleSetSection('pagos-historial')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'pagos-historial' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <Clock className="w-4 h-4" />
                    <span>Historial de Pagos</span>
                  </button>
                  <button
                    onClick={() => handleSetSection('pagos-cargo')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'pagos-cargo' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <Receipt className="w-4 h-4" />
                    <span>Crear Cargo</span>
                  </button>
                  <button
                    onClick={() => handleSetSection('pagos-excepcion')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'pagos-excepcion' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <Percent className="w-4 h-4" />
                    <span>Crear Excepción</span>
                  </button>
                </div>
              )}
            </>
          )}
          
          {/* Contabilidad Menu (Premium) */}
          {canEdit && (
            <>
              <button
                onClick={() => {
                  if (clubActual?.tiene_membresia) {
                    setMenuContabilidadOpen(!menuContabilidadOpen);
                  } else {
                    toast.info('Función Premium - Activa tu membresía para acceder');
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection.startsWith('contabilidad-') ? 'bg-blue-600 text-white' : clubActual?.tiene_membresia ? 'text-slate-400 hover:bg-slate-700 hover:text-white' : 'text-slate-500 hover:bg-slate-700'}`}
              >
                <BookOpen className="w-5 h-5" />
                <span>Contabilidad</span>
                {!clubActual?.tiene_membresia && (
                  <span className="ml-auto text-xs bg-amber-600/30 text-amber-400 px-1.5 py-0.5 rounded">PRO</span>
                )}
                {clubActual?.tiene_membresia && (
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${menuContabilidadOpen ? 'rotate-180' : ''}`} />
                )}
              </button>
              {menuContabilidadOpen && clubActual?.tiene_membresia && (
                <div className="ml-4 pl-4 border-l border-slate-600 space-y-1">
                  <button
                    onClick={() => setActiveSection('contabilidad-ingresos')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'contabilidad-ingresos' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>Ingresos</span>
                  </button>
                  <button
                    onClick={() => setActiveSection('contabilidad-egresos')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'contabilidad-egresos' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <TrendingDown className="w-4 h-4" />
                    <span>Egresos</span>
                  </button>
                  <button
                    onClick={() => setActiveSection('contabilidad-cuentas')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'contabilidad-cuentas' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Cuentas por Pagar</span>
                  </button>
                </div>
              )}
            </>
          )}
          
          {/* Finanzas Menu */}
          {canEdit && (
            <>
              <button
                onClick={() => setMenuFinanzasOpen(!menuFinanzasOpen)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection.startsWith('finanzas') ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
              >
                <PiggyBank className="w-5 h-5" />
                <span>Finanzas</span>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${menuFinanzasOpen ? 'rotate-180' : ''}`} />
              </button>
              {menuFinanzasOpen && (
                <div className="ml-4 pl-4 border-l border-slate-600 space-y-1">
                  <button
                    onClick={() => handleSetSection('finanzas')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'finanzas' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>Resumen Financiero</span>
                  </button>
                  <button
                    onClick={() => handleSetSection('finanzas-morosidad')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'finanzas-morosidad' ? 'bg-blue-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                  >
                    <AlertTriangle className="w-4 h-4" />
                    <span>Morosidad</span>
                  </button>
                </div>
              )}
            </>
          )}
          
          {canEdit && inscripcionesPendientes.length > 0 && (
            <button
              onClick={() => setActiveSection('pendientes')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'pendientes' ? 'bg-blue-600 text-white' : 'text-yellow-500 hover:bg-slate-700'}`}
            >
              <Bell className="w-5 h-5" />
              <span>Pendientes</span>
              <span className="ml-auto text-xs bg-yellow-600 text-white px-2 py-0.5 rounded-full">{inscripcionesPendientes.length}</span>
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => handleSetSection('usuarios')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'usuarios' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
            >
              <UserCog className="w-5 h-5" />
              <span>Usuarios</span>
            </button>
          )}
          <button
            onClick={() => handleSetSection('configuracion')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'configuracion' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
          >
            <Settings className="w-5 h-5" />
            <span>Configuración</span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Dashboard */}
        {activeSection === 'dashboard' && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Inicio</h1>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 bg-gradient-to-br from-blue-600 to-blue-700 border-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-200 text-sm">Total Integrantes</p>
                    <p className="text-4xl font-bold text-white mt-1">{integrantes.length}</p>
                  </div>
                  <Users className="w-12 h-12 text-blue-300" />
                </div>
              </Card>
              <Card className="p-6 bg-gradient-to-br from-blue-600 to-blue-700 border-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-200 text-sm">Categorías</p>
                    <p className="text-4xl font-bold text-white mt-1">{categorias.length}</p>
                  </div>
                  <FolderTree className="w-12 h-12 text-blue-300" />
                </div>
              </Card>
              <Card className="p-6 bg-gradient-to-br from-emerald-600 to-emerald-700 border-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-200 text-sm">Activos</p>
                    <p className="text-4xl font-bold text-white mt-1">{integrantes.filter(i => i.estado === 'activo').length}</p>
                  </div>
                  <Shield className="w-12 h-12 text-emerald-300" />
                </div>
              </Card>
            </div>

            {/* Resumen por Categoría */}
            <h2 className="text-xl font-semibold text-white mt-8 mb-4">Integrantes por Categoría</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorias.map(cat => {
                const count = integrantes.filter(i => i.categoria_ids?.includes(cat.id)).length;
                return (
                  <Card key={cat.id} className="p-4 bg-slate-800 border-slate-700 hover:border-blue-500 transition-colors cursor-pointer" onClick={() => { setCategoriaFiltro(cat.id); setActiveSection('integrantes'); }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-white">{cat.nombre}</h3>
                        {cat.edad_minima && cat.edad_maxima && (
                          <p className="text-xs text-slate-400">{cat.edad_minima} - {cat.edad_maxima} años</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-400">{count}</p>
                        <p className="text-xs text-slate-400">integrantes</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Integrantes */}
        {activeSection === 'integrantes' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-white">Integrantes</h1>
              <div className="flex gap-2">
                {/* Toggle de vista */}
                <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                  <button
                    onClick={() => toggleViewMode('cards')}
                    className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    title="Vista de tarjetas"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleViewMode('list')}
                    className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    title="Vista de lista"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-600 disabled:opacity-50"
                    title="Actualizar"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <Button variant="outline" onClick={() => setShowExportDialog(true)} className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="export-excel-btn">
                  <Download className="w-4 h-4 mr-2" /> Exportar Excel
                </Button>
                {canEdit && (
                  <Button onClick={() => { setEditingItem(null); setIntegranteForm({ nombre: '', apellido: '', rut: '', fecha_nacimiento: '', email: '', telefono: '', direccion: '', categoria_ids: [], fecha_ingreso: '', observaciones: '', foto: '' }); setShowIntegranteDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" /> Nuevo Integrante
                  </Button>
                )}
              </div>
            </div>

            {/* Alerta de integrantes bloqueados */}
            {limitesPlan.integrantesBloqueados > 0 && (
              <Card className="p-4 bg-amber-600/20 border-amber-500/50 mb-6">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-amber-400" />
                  <div className="flex-1">
                    <p className="text-amber-200 font-medium">
                      {limitesPlan.integrantesBloqueados} integrantes ocultos (Plan Free)
                    </p>
                    <p className="text-amber-200/70 text-sm">
                      Mostrando {integrantes.length} de {limitesPlan.totalIntegrantes} integrantes. Actualiza a Pro para ver todos.
                    </p>
                  </div>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleOpenUpgrade}>
                    Actualizar a Pro
                  </Button>
                </div>
              </Card>
            )}

            {/* Filtros */}
            <Card className="p-4 bg-slate-800 border-slate-700 mb-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Buscar por nombre o RUT..." 
                      value={searchTerm} 
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <Select value={categoriaFiltro} onValueChange={setCategoriaFiltro}>
                  <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600 text-white">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas las categorías</SelectItem>
                    <SelectItem value="sin_categoria">Sin categoría</SelectItem>
                    {categorias.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={ordenarPor} onValueChange={setOrdenarPor}>
                  <SelectTrigger className="w-[160px] bg-slate-700 border-slate-600 text-white">
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apellido">Apellido</SelectItem>
                    <SelectItem value="nombre">Nombre</SelectItem>
                    <SelectItem value="rut">RUT</SelectItem>
                    <SelectItem value="fecha_nacimiento">Fecha Nac.</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={estadoFiltro} onValueChange={setEstadoFiltro}>
                  <SelectTrigger className="w-[160px] bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activos ({integrantesActivos})</SelectItem>
                    <SelectItem value="suspendido">Suspendidos ({integrantesSuspendidos})</SelectItem>
                    <SelectItem value="todos">Todos</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" onClick={() => setOrdenDir(ordenDir === 'asc' ? 'desc' : 'asc')} className="text-slate-400 hover:text-white">
                  <ArrowUpDown className={`w-4 h-4 ${ordenDir === 'desc' ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            </Card>

            {/* Integrantes - Vista condicional */}
            {viewMode === 'list' ? (
              <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Nombre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">RUT</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Fecha Nac.</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Categorías</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Estado</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {integrantesFiltrados.map(int => (
                        <tr key={int.id} className="hover:bg-slate-700/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <IntegranteAvatar integrante={int} />
                              <div>
                                <p className="font-medium text-white">{int.apellido}, {int.nombre}</p>
                                {int.email && <p className="text-xs text-slate-400">{int.email}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-300">{int.rut || '-'}</td>
                          <td className="px-4 py-3 text-slate-300">{int.fecha_nacimiento || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {int.categorias_info?.map(c => (
                                <Badge key={c.id} variant="secondary" className="bg-slate-600 text-slate-200 text-xs">{c.nombre}</Badge>
                              )) || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {int.estado === 'suspendido' ? (
                              <div>
                                <Badge className="bg-amber-600">Suspendido</Badge>
                                {int.motivo_suspension && (
                                  <p className="text-xs text-amber-400 mt-1">
                                    {int.motivo_suspension === 'retiro_voluntario' && 'Retiro voluntario'}
                                    {int.motivo_suspension === 'deuda' && 'Por deuda'}
                                    {int.motivo_suspension === 'inasistencia' && 'Por inasistencia'}
                                    {int.motivo_suspension === 'otro' && 'Otro motivo'}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <Badge className="bg-emerald-600">Activo</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleVerCartola(int)} className="text-slate-400 hover:text-green-400" title="Ver Cartola">
                                <Receipt className="w-4 h-4" />
                              </Button>
                              {canEdit && (
                                <>
                                  <Button variant="ghost" size="icon" onClick={() => { setEditingItem(int); setIntegranteForm({ ...int, categoria_ids: int.categoria_ids || [] }); setShowIntegranteDialog(true); }} className="text-slate-400 hover:text-white" title="Editar">
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  {int.estado === 'suspendido' ? (
                                    <Button variant="ghost" size="icon" onClick={() => handleReactivarIntegrante(int)} className="text-slate-400 hover:text-emerald-400" title="Reactivar">
                                      <UserCheck className="w-4 h-4" />
                                    </Button>
                                  ) : (
                                    <Button variant="ghost" size="icon" onClick={() => { setIntegranteASuspender(int); setShowSuspenderDialog(true); }} className="text-slate-400 hover:text-amber-400" title="Suspender">
                                      <UserX className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteIntegrante(int.id)} className="text-slate-400 hover:text-red-400" title="Eliminar">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {integrantesFiltrados.length === 0 && (
                    <div className="p-12 text-center">
                      <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No se encontraron integrantes</p>
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              /* Vista de tarjetas */
              integrantesFiltrados.length === 0 ? (
                <Card className="p-12 bg-slate-800 border-slate-700 text-center">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No se encontraron integrantes</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {integrantesFiltrados.map(int => (
                    <Card key={int.id} className={`p-4 bg-slate-800 border-slate-700 ${int.estado === 'suspendido' ? 'opacity-70' : ''}`}>
                      <div className="flex items-start gap-3">
                        <IntegranteAvatar integrante={int} size="lg" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{int.nombre} {int.apellido}</h3>
                          <p className="text-sm text-slate-400 truncate">{int.email || int.telefono || 'Sin contacto'}</p>
                          <p className="text-xs text-slate-500">{int.rut || 'Sin RUT'}</p>
                        </div>
                      </div>
                      
                      {/* Categorías */}
                      {int.categorias_info?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {int.categorias_info.map(c => (
                            <Badge key={c.id} variant="secondary" className="bg-slate-600 text-slate-200 text-xs">{c.nombre}</Badge>
                          ))}
                        </div>
                      )}
                      
                      <div className="mt-3 flex items-center gap-2">
                        {int.estado === 'suspendido' ? (
                          <Badge className="bg-amber-600">Suspendido</Badge>
                        ) : (
                          <Badge className="bg-emerald-600">Activo</Badge>
                        )}
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="mt-3 pt-3 border-t border-slate-700 flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="flex-1 text-slate-400 hover:text-green-400 hover:bg-green-600/10"
                          onClick={() => handleVerCartola(int)}
                        >
                          <Receipt className="w-3 h-3 mr-1" /> Cartola
                        </Button>
                        {canEdit && (
                          <>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-slate-400 hover:text-white hover:bg-slate-600"
                              onClick={() => { setEditingItem(int); setIntegranteForm({ ...int, categoria_ids: int.categoria_ids || [] }); setShowIntegranteDialog(true); }}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            {int.estado === 'suspendido' ? (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-slate-400 hover:text-emerald-400 hover:bg-emerald-600/10"
                                onClick={() => handleReactivarIntegrante(int)}
                              >
                                <UserCheck className="w-3 h-3" />
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-slate-400 hover:text-amber-400 hover:bg-amber-600/10"
                                onClick={() => { setIntegranteASuspender(int); setShowSuspenderDialog(true); }}
                              >
                                <UserX className="w-3 h-3" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-slate-400 hover:text-red-400 hover:bg-red-600/10"
                              onClick={() => handleDeleteIntegrante(int.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {/* Categorías */}
        {activeSection === 'categorias' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-white">Categorías</h1>
              <div className="flex gap-2">
                {/* Toggle de vista */}
                <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                  <button
                    onClick={() => toggleViewMode('cards')}
                    className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    title="Vista de tarjetas"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleViewMode('list')}
                    className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    title="Vista de lista"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-600 disabled:opacity-50"
                    title="Actualizar"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {canEdit && (
                  <Button onClick={() => { setEditingItem(null); setCategoriaForm({ nombre: '', descripcion: '', edad_minima: '', edad_maxima: '' }); setShowCategoriaDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" /> Nueva Categoría
                  </Button>
                )}
              </div>
            </div>

            {/* Filtro de búsqueda de categorías */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar categoría..."
                value={searchCategoriaClub}
                onChange={(e) => setSearchCategoriaClub(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700 text-white"
              />
            </div>

            {/* Alerta de categorías bloqueadas */}
            {limitesPlan.categoriasBloqueadas > 0 && (
              <Card className="p-4 bg-amber-600/20 border-amber-500/50 mb-6">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-amber-400" />
                  <div className="flex-1">
                    <p className="text-amber-200 font-medium">
                      {limitesPlan.categoriasBloqueadas} categorías bloqueadas (Plan Free)
                    </p>
                    <p className="text-amber-200/70 text-sm">
                      El plan Free permite solo 1 categoría. Actualiza a Pro para desbloquear las {limitesPlan.categoriasBloqueadas} categorías restantes.
                    </p>
                  </div>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleOpenUpgrade}>
                    Actualizar a Pro
                  </Button>
                </div>
              </Card>
            )}

            {/* Vista de categorías - si hay una seleccionada, mostrar sus integrantes */}
            {categoriaSeleccionada ? (
              <div>
                {/* Header de categoría seleccionada */}
                <Card className="p-4 bg-slate-800 border-slate-700 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button variant="outline" onClick={() => setCategoriaSeleccionada(null)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Volver
                      </Button>
                      <div>
                        <h2 className="text-xl font-bold text-white">{categoriaSeleccionada.nombre}</h2>
                        {categoriaSeleccionada.descripcion && (
                          <p className="text-sm text-slate-400">{categoriaSeleccionada.descripcion}</p>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-2">
                        <Button onClick={handleGenerarLinkInscripcion} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          <Link2 className="w-4 h-4 mr-2" /> Link Inscripción
                        </Button>
                        <Button onClick={() => setShowImportarDialog(true)} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          <Upload className="w-4 h-4 mr-2" /> Importar Excel
                        </Button>
                        <Button onClick={() => setShowAsociarDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                          <UserPlus className="w-4 h-4 mr-2" /> Agregar Integrantes
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Filtros de categoría */}
                <Card className="p-4 bg-slate-800 border-slate-700 mb-4">
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input 
                          placeholder="Buscar por nombre o RUT..." 
                          value={searchTermCategoria} 
                          onChange={(e) => setSearchTermCategoria(e.target.value)}
                          className="pl-10 bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    <Select value={ordenarPorCategoria} onValueChange={setOrdenarPorCategoria}>
                      <SelectTrigger className="w-[160px] bg-slate-700 border-slate-600 text-white">
                        <ArrowUpDown className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Ordenar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="apellido">Apellido</SelectItem>
                        <SelectItem value="nombre">Nombre</SelectItem>
                        <SelectItem value="rut">RUT</SelectItem>
                        <SelectItem value="fecha_nacimiento">Fecha Nac.</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setOrdenDirCategoria(ordenDirCategoria === 'asc' ? 'desc' : 'asc')}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      <ArrowUpDown className={`w-4 h-4 transition-transform ${ordenDirCategoria === 'desc' ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                </Card>

                {/* Lista de integrantes de esta categoría */}
                <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                  <div className="p-4 border-b border-slate-700 bg-slate-700/30">
                    <h3 className="font-semibold text-white">
                      Integrantes en "{categoriaSeleccionada.nombre}" ({getIntegrantesDeCategoria(categoriaSeleccionada.id).length})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Nombre</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">RUT</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Fecha Nac.</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Email</th>
                          {canEdit && <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Acciones</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {getIntegrantesDeCategoria(categoriaSeleccionada.id).map(int => (
                          <tr key={int.id} className="hover:bg-slate-700/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-600/30 flex items-center justify-center text-blue-400 font-bold text-sm">
                                  {int.nombre?.charAt(0)}{int.apellido?.charAt(0)}
                                </div>
                                <span className="font-medium text-white">{int.apellido}, {int.nombre}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-300">{int.rut || '-'}</td>
                            <td className="px-4 py-3 text-slate-300">{int.fecha_nacimiento || '-'}</td>
                            <td className="px-4 py-3 text-slate-300">{int.email || '-'}</td>
                            {canEdit && (
                              <td className="px-4 py-3 text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleToggleIntegranteCategoria(int, categoriaSeleccionada.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" /> Quitar
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {getIntegrantesDeCategoria(categoriaSeleccionada.id).length === 0 && (
                      <div className="p-12 text-center">
                        <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">No hay integrantes en esta categoría</p>
                        {canEdit && (
                          <Button onClick={() => setShowAsociarDialog(true)} variant="outline" className="mt-4 border-slate-600 text-slate-300">
                            <UserPlus className="w-4 h-4 mr-2" /> Agregar Integrantes
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            ) : (
              /* Grid o Lista de categorías */
              (() => {
                const categoriasFiltradas = categorias.filter(cat => 
                  !searchCategoriaClub || cat.nombre?.toLowerCase().includes(searchCategoriaClub.toLowerCase())
                );
                
                return categoriasFiltradas.length === 0 ? (
                  <Card className="p-12 bg-slate-800 border-slate-700 text-center">
                    <Search className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No se encontraron categorías</p>
                  </Card>
                ) : viewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoriasFiltradas.map(cat => {
                    const integrantesCat = integrantes.filter(i => i.categoria_ids?.includes(cat.id));
                    const isBloqueada = cat.bloqueada === true;
                    return (
                      <Card 
                        key={cat.id} 
                        className={`p-4 bg-slate-800 border-slate-700 ${isBloqueada ? 'opacity-70' : ''}`}
                        data-testid={`categoria-card-${cat.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div 
                            className={`w-12 h-12 rounded-full ${isBloqueada ? 'bg-amber-600/30' : 'bg-blue-600'} flex items-center justify-center shrink-0 ${!isBloqueada ? 'cursor-pointer hover:bg-blue-700 transition-colors' : ''}`}
                            onClick={() => { 
                              if (isBloqueada) {
                                toast.error('Categoría bloqueada. Actualiza a Pro para acceder.');
                                return;
                              }
                              setCategoriaSeleccionada(cat); 
                              setSearchTermCategoria(''); 
                            }}
                          >
                            {isBloqueada ? (
                              <Lock className="w-5 h-5 text-amber-400" />
                            ) : (
                              <FolderTree className="w-5 h-5 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 
                              className={`font-semibold text-white truncate ${!isBloqueada ? 'cursor-pointer hover:text-blue-400' : ''}`}
                              onClick={() => { 
                                if (isBloqueada) {
                                  toast.error('Categoría bloqueada. Actualiza a Pro para acceder.');
                                  return;
                                }
                                setCategoriaSeleccionada(cat); 
                                setSearchTermCategoria(''); 
                              }}
                            >
                              {cat.nombre}
                            </h3>
                            <p className="text-sm text-slate-400 truncate">{cat.descripcion || 'Sin descripción'}</p>
                            <p className="text-xs text-slate-500">{isBloqueada ? '?' : integrantesCat.length} integrantes</p>
                          </div>
                        </div>
                        
                        {/* Badges */}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {isBloqueada ? (
                            <Badge className="bg-amber-600/30 text-amber-400 border border-amber-600/50">
                              <Lock className="w-3 h-3 mr-1" /> Bloqueada
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-600/30 text-emerald-400 border border-emerald-600/50">Activa</Badge>
                          )}
                          {(cat.edad_minima || cat.edad_maxima) && (
                            <Badge variant="secondary" className="bg-slate-600 text-slate-200 text-xs">
                              {cat.edad_minima || '0'} - {cat.edad_maxima || '99'} años
                            </Badge>
                          )}
                        </div>
                        
                        {/* Botones de acción */}
                        <div className="mt-3 pt-3 border-t border-slate-700 flex gap-2">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className={`flex-1 ${isBloqueada ? 'text-amber-400 hover:bg-amber-600/10' : 'text-slate-400 hover:text-blue-400 hover:bg-blue-600/10'}`}
                            onClick={() => { 
                              if (isBloqueada) {
                                toast.error('Categoría bloqueada. Actualiza a Pro para acceder.');
                                return;
                              }
                              setCategoriaSeleccionada(cat); 
                              setSearchTermCategoria(''); 
                            }}
                          >
                            {isBloqueada ? (
                              <>
                                <Lock className="w-3 h-3 mr-1" /> Bloqueada
                              </>
                            ) : (
                              <>
                                <Eye className="w-3 h-3 mr-1" /> Ver
                              </>
                            )}
                          </Button>
                          {canEdit && !isBloqueada && (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-slate-400 hover:text-white hover:bg-slate-600"
                                onClick={() => { setEditingItem(cat); setCategoriaForm({ nombre: cat.nombre, descripcion: cat.descripcion || '', edad_minima: cat.edad_minima?.toString() || '', edad_maxima: cat.edad_maxima?.toString() || '' }); setShowCategoriaDialog(true); }}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-slate-400 hover:text-red-400 hover:bg-red-600/10"
                                onClick={() => handleDeleteCategoria(cat.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                  {categorias.length === 0 && (
                    <div className="col-span-full p-12 text-center">
                      <FolderTree className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No hay categorías creadas</p>
                      {canEdit && (
                        <Button onClick={() => { setEditingItem(null); setCategoriaForm({ nombre: '', descripcion: '', edad_minima: '', edad_maxima: '' }); setShowCategoriaDialog(true); }} className="mt-4 bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4 mr-2" /> Crear Primera Categoría
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Vista de lista de categorías */
                <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Categoría</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden md:table-cell">Descripción</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden lg:table-cell">Rango Edad</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Integrantes</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {categoriasFiltradas.map(cat => {
                        const integrantesCat = integrantes.filter(i => i.categoria_ids?.includes(cat.id));
                        const isBloqueada = cat.bloqueada === true;
                        return (
                          <tr 
                            key={cat.id} 
                            className={`hover:bg-slate-700/30 ${isBloqueada ? 'opacity-60' : 'cursor-pointer'}`}
                            onClick={() => {
                              if (!isBloqueada) {
                                setCategoriaSeleccionada(cat);
                                setSearchTermCategoria('');
                              }
                            }}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-white">{cat.nombre}</p>
                                {isBloqueada && (
                                  <Badge className="bg-amber-600/30 text-amber-400 text-xs">
                                    <Lock className="w-3 h-3 mr-1" /> Bloqueada
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{cat.descripcion || '-'}</td>
                            <td className="px-4 py-3 text-blue-400 hidden lg:table-cell">
                              {(cat.edad_minima || cat.edad_maxima) ? `${cat.edad_minima || '0'} - ${cat.edad_maxima || '99'} años` : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className="bg-slate-700">{isBloqueada ? '?' : integrantesCat.length}</Badge>
                            </td>
                            <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                              {canEdit && !isBloqueada && (
                                <div className="flex justify-end gap-1">
                                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => { setEditingItem(cat); setCategoriaForm({ nombre: cat.nombre, descripcion: cat.descripcion || '', edad_minima: cat.edad_minima?.toString() || '', edad_maxima: cat.edad_maxima?.toString() || '' }); setShowCategoriaDialog(true); }}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400" onClick={() => handleDeleteCategoria(cat.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Card>
              );
              })()
            )}
          </div>
        )}

        {/* Usuarios */}
        {activeSection === 'usuarios' && isAdmin && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-white">Usuarios del Club</h1>
              <Button onClick={() => setShowInvitarDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                <UserPlus className="w-4 h-4 mr-2" /> Invitar Usuario
              </Button>
            </div>

            <Card className="bg-slate-800 border-slate-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Usuario</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Rol</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {usuariosClub.map(uc => {
                    const rol = uc.role || uc.rol;
                    const esAdmin = uc.es_admin || rol === 'admin';
                    const esMismo = uc.user_id === user?.id;
                    const puedeRemover = !esAdmin && !esMismo && uc.puede_eliminar !== false;
                    
                    return (
                      <tr key={uc.id} className="hover:bg-slate-700/50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-white">{uc.nombre || uc.email}</p>
                          <p className="text-xs text-slate-400">{uc.email}</p>
                          {uc.es_invitacion_pendiente && (
                            <Badge className="mt-1 bg-yellow-600/20 text-yellow-400 text-xs">Pendiente de aceptar</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {esAdmin ? (
                            <Badge className="bg-amber-600">Administrador</Badge>
                          ) : !esMismo ? (
                            <Select 
                              value={rol} 
                              onValueChange={async (nuevoRol) => {
                                try {
                                  await axios.put(`${API}/clubes-deportivos/${clubActual.id}/usuarios/${uc.id}`, 
                                    { role: nuevoRol }, 
                                    { withCredentials: true }
                                  );
                                  toast.success('Rol actualizado');
                                  const res = await axios.get(`${API}/clubes-deportivos/${clubActual.id}/usuarios`, { withCredentials: true });
                                  setUsuariosClub(res.data);
                                } catch (error) {
                                  toast.error('Error al cambiar rol');
                                }
                              }}
                            >
                              <SelectTrigger className="w-36 h-8 bg-slate-700 border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="super_usuario">Super Usuario</SelectItem>
                                <SelectItem value="lectura">Solo Lectura</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={rol === 'super_usuario' ? 'bg-blue-600' : 'bg-slate-600'}>
                              {rol === 'super_usuario' ? 'Super Usuario' : 'Solo Lectura'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {puedeRemover && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-400 hover:text-red-300 hover:bg-red-600/20"
                              onClick={async () => {
                                if (window.confirm(`¿Remover a ${uc.nombre || uc.email} del club?`)) {
                                  try {
                                    await axios.delete(`${API}/clubes-deportivos/${clubActual.id}/usuarios/${uc.id}`, { withCredentials: true });
                                    toast.success('Usuario removido del club');
                                    const res = await axios.get(`${API}/clubes-deportivos/${clubActual.id}/usuarios`, { withCredentials: true });
                                    setUsuariosClub(res.data);
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Error al remover usuario');
                                  }
                                }
                              }}
                            >
                              Remover
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {/* Inscripciones Pendientes */}
        {activeSection === 'pendientes' && canEdit && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-white">Inscripciones Pendientes</h1>
              <Badge className="bg-yellow-600">{inscripcionesPendientes.length} solicitudes</Badge>
            </div>

            {inscripcionesPendientes.length === 0 ? (
              <Card className="p-12 bg-slate-800 border-slate-700 text-center">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">¡Todo al día!</h3>
                <p className="text-slate-400">No hay inscripciones pendientes de aprobación</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {inscripcionesPendientes.map(p => (
                  <Card key={p.id} className="p-4 bg-slate-800 border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-yellow-600/30 flex items-center justify-center">
                          <Clock className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{p.apellido}, {p.nombre}</h3>
                          <p className="text-sm text-slate-400">RUT: {p.rut}</p>
                          {p.email && <p className="text-sm text-slate-400">{p.email}</p>}
                          {p.categorias_info && (
                            <div className="flex gap-1 mt-1">
                              {p.categorias_info.map(c => (
                                <Badge key={c.id} variant="outline" className="text-xs border-blue-500 text-blue-400">{c.nombre}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleRechazarInscripcion(p.id)} 
                          variant="outline" 
                          className="border-red-600 text-red-400 hover:bg-red-600/20"
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Rechazar
                        </Button>
                        <Button 
                          onClick={() => handleAprobarInscripcion(p.id)} 
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Finanzas - Resumen (página principal) */}
        {/* Finanzas Dashboard (consolidado) */}
        {activeSection === 'finanzas' && canEdit && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Resumen Financiero</h1>
            <FinanzasModule clubId={clubActual.id} canEdit={canEdit} categorias={categorias} integrantes={integrantes} section="resumen" />
          </div>
        )}

        {/* Finanzas - Morosidad */}
        {activeSection === 'finanzas-morosidad' && canEdit && (
          <MorosidadSection clubId={clubActual.id} categorias={categorias} onVerCartola={handleVerCartola} />
        )}

        {/* Planes - Planes de Cuotas */}
        {activeSection === 'planes-cuotas' && canEdit && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Planes de Cuotas</h1>
            <FinanzasModule clubId={clubActual.id} canEdit={canEdit} categorias={categorias} integrantes={integrantes} section="planes" viewMode={viewMode} onToggleViewMode={toggleViewMode} onRefresh={handleRefresh} refreshing={refreshing} />
          </div>
        )}

        {/* Planes - Asignar Individual */}
        {activeSection === 'planes-asignar-individual' && canEdit && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Asignar Plan - Individual</h1>
            <FinanzasModule clubId={clubActual.id} canEdit={canEdit} categorias={categorias} integrantes={integrantes} section="asignar-individual" />
          </div>
        )}

        {/* Planes - Asignar Masivo */}
        {activeSection === 'planes-asignar-masivo' && canEdit && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Asignar Plan - Masivo</h1>
            <FinanzasModule clubId={clubActual.id} canEdit={canEdit} categorias={categorias} integrantes={integrantes} section="asignar-masivo" />
          </div>
        )}

        {/* Pagos - Registrar Ingreso */}
        {activeSection === 'pagos-ingresos' && canEdit && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Registrar Ingreso</h1>
            <FinanzasModule clubId={clubActual.id} canEdit={canEdit} categorias={categorias} integrantes={integrantes} section="ingresos" />
          </div>
        )}

        {/* Pagos - Historial */}
        {activeSection === 'pagos-historial' && canEdit && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Historial de Pagos</h1>
            <FinanzasModule clubId={clubActual.id} canEdit={canEdit} categorias={categorias} integrantes={integrantes} section="historial" />
          </div>
        )}

        {/* Pagos - Crear Cargo */}
        {activeSection === 'pagos-cargo' && canEdit && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Crear Cargo Excepcional</h1>
            <FinanzasModule clubId={clubActual.id} canEdit={canEdit} categorias={categorias} integrantes={integrantes} section="cargo" />
          </div>
        )}

        {/* Pagos - Crear Excepción */}
        {activeSection === 'pagos-excepcion' && canEdit && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Crear Excepción de Pago</h1>
            <FinanzasModule clubId={clubActual.id} canEdit={canEdit} categorias={categorias} integrantes={integrantes} section="excepcion" />
          </div>
        )}

        {/* Contabilidad - Ingresos */}
        {activeSection === 'contabilidad-ingresos' && canEdit && clubActual?.tiene_membresia && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Ingresos del Club</h1>
            <ContabilidadModule clubId={clubActual.id} section="ingresos" />
          </div>
        )}

        {/* Contabilidad - Egresos */}
        {activeSection === 'contabilidad-egresos' && canEdit && clubActual?.tiene_membresia && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Egresos del Club</h1>
            <ContabilidadModule clubId={clubActual.id} section="egresos" />
          </div>
        )}

        {/* Contabilidad - Cuentas por Pagar */}
        {activeSection === 'contabilidad-cuentas' && canEdit && clubActual?.tiene_membresia && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Cuentas por Pagar</h1>
            <ContabilidadModule clubId={clubActual.id} section="cuentas" />
          </div>
        )}

        {/* Configuración */}
        {activeSection === 'configuracion' && (
          <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Configuración del Club</h1>
            <Card className="p-6 bg-slate-800 border-slate-700 max-w-2xl">
              <div className="flex items-center gap-4 mb-6">
                {clubActual.logo ? (
                  <img src={clubActual.logo} alt="Logo" className="w-24 h-24 rounded-xl object-cover" />
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-blue-600 flex items-center justify-center">
                    <DisciplinaIcon className="w-12 h-12 text-white" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-white">{clubActual.nombre}</h2>
                  <p className="text-blue-400">{disciplinas.find(d => d.id === clubActual.disciplina)?.nombre}</p>
                </div>
              </div>
              
              {canEdit && (
                <Button onClick={() => { setEditingItem(clubActual); setClubForm({ nombre: clubActual.nombre, disciplina: clubActual.disciplina || 'otro', pais: clubActual.pais || 'CL', descripcion: clubActual.descripcion || '', direccion: clubActual.direccion || '', telefono: clubActual.telefono || '', email: clubActual.email || '', logo: clubActual.logo || '', texto_bienvenida: clubActual.texto_bienvenida || '' }); setShowClubDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
                  <Pencil className="w-4 h-4 mr-2" /> Editar Información
                </Button>
              )}
            </Card>

            {/* Link de Consulta */}
            <Card className="p-6 bg-slate-800 border-slate-700 max-w-2xl mt-6">
              <h3 className="text-lg font-semibold text-white mb-2">Portal de Consulta para Integrantes</h3>
              <p className="text-sm text-slate-400 mb-4">
                Comparte este link con tus integrantes para que puedan consultar su estado de pagos usando su RUT y correo electrónico.
              </p>
              <Button onClick={handleGetLinkConsulta} variant="outline" className="border-blue-500 text-blue-400 hover:bg-blue-600/20">
                <Copy className="w-4 h-4 mr-2" /> Copiar Link de Consulta
              </Button>
            </Card>

            {/* Datos Bancarios para Pagos */}
            <Card className="p-6 bg-slate-800 border-slate-700 max-w-2xl mt-6" data-testid="datos-bancarios-card">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-green-400" />
                <h3 className="text-lg font-semibold text-white">Datos Bancarios para Pagos</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Configura los datos de tu cuenta bancaria para que los integrantes puedan realizar transferencias y el sistema las confirme automáticamente.
              </p>
              
              {datosBancarios ? (
                <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-400">Banco:</span>
                      <span className="text-white ml-2">{datosBancarios.banco}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Tipo:</span>
                      <span className="text-white ml-2">{datosBancarios.tipo_cuenta}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">N° Cuenta:</span>
                      <span className="text-white ml-2">{datosBancarios.numero_cuenta}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">RUT Titular:</span>
                      <span className="text-white ml-2">{datosBancarios.rut_titular}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-400">Nombre Titular:</span>
                      <span className="text-white ml-2">{datosBancarios.nombre_titular}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">No has configurado tus datos bancarios aún</span>
                  </div>
                </div>
              )}

              {/* Código de Pago para Transferencias */}
              {clubActual?.codigo_pago && (
                <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-4 mb-4">
                  <h4 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Código de Pago
                  </h4>
                  <div className="flex items-center gap-3 mb-2">
                    <code className="bg-slate-800 px-3 py-2 rounded text-lg font-mono text-white">
                      {clubActual.codigo_pago}
                    </code>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-emerald-600"
                      onClick={() => {
                        navigator.clipboard.writeText(clubActual.codigo_pago);
                        toast.success('Código copiado');
                      }}
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copiar
                    </Button>
                  </div>
                  <p className="text-sm text-slate-400">
                    Indica a tus integrantes que al transferir, pongan en el motivo:
                  </p>
                  <p className="text-sm text-white mt-1 font-medium">
                    {clubActual.codigo_pago} + RUT del integrante
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Ejemplo: <span className="text-slate-300">{clubActual.codigo_pago} 12345678-9</span>
                  </p>
                </div>
              )}
              
              {canEdit && (
                <Button 
                  onClick={() => {
                    if (datosBancarios) {
                      setDatosBancariosForm({
                        banco: datosBancarios.banco || '',
                        tipo_cuenta: datosBancarios.tipo_cuenta || 'corriente',
                        numero_cuenta: datosBancarios.numero_cuenta || '',
                        rut_titular: datosBancarios.rut_titular || '',
                        nombre_titular: datosBancarios.nombre_titular || '',
                        email_notificacion: datosBancarios.email_notificacion || ''
                      });
                    } else {
                      setDatosBancariosForm({
                        banco: '',
                        tipo_cuenta: 'corriente',
                        numero_cuenta: '',
                        rut_titular: '',
                        nombre_titular: '',
                        email_notificacion: ''
                      });
                    }
                    setShowDatosBancariosDialog(true);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="configurar-datos-bancarios-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" /> {datosBancarios ? 'Editar Datos Bancarios' : 'Configurar Datos Bancarios'}
                </Button>
              )}
            </Card>

            {/* Mi Cuenta - Preferencias */}
            <Card className="p-6 bg-slate-800 border-slate-700 max-w-2xl mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Mi Cuenta</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
                    {user?.name?.charAt(0) || user?.email?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-medium">{user?.name}</p>
                    <p className="text-slate-400 text-sm">{user?.email}</p>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Preferencias de Comunicación</h4>
                  
                  <div className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <input
                      type="checkbox"
                      id="marketing-pref"
                      checked={user?.acepto_marketing || false}
                      onChange={async (e) => {
                        try {
                          await axios.post(`${API}/auth/actualizar-marketing`, {
                            acepto_marketing: e.target.checked
                          }, { withCredentials: true });
                          toast.success(e.target.checked ? 'Suscrito a comunicaciones comerciales' : 'Dado de baja de comunicaciones comerciales');
                          // Recargar usuario
                          window.location.reload();
                        } catch (error) {
                          toast.error('Error al actualizar preferencia');
                        }
                      }}
                      className="mt-1 w-5 h-5 rounded border-slate-500 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <label htmlFor="marketing-pref" className="text-white cursor-pointer">
                        Recibir comunicaciones comerciales deportivas
                      </label>
                      <p className="text-sm text-slate-400 mt-1">
                        Seguros deportivos, equipamiento, indumentaria, suplementación y promociones de Kinosis.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Documentos Legales</h4>
                  <div className="flex gap-4">
                    <a href="/terminos" target="_blank" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
                      <FileText className="w-4 h-4" /> Términos y Condiciones
                    </a>
                    <a href="/privacidad" target="_blank" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
                      <Shield className="w-4 h-4" /> Política de Privacidad
                    </a>
                  </div>
                  {user?.fecha_acepto_terminos && (
                    <p className="text-xs text-slate-500 mt-2">
                      Términos aceptados el {new Date(user.fecha_acepto_terminos).toLocaleDateString('es-CL')}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}
      </main>

      {/* Dialog Club */}
      <Dialog open={showClubDialog} onOpenChange={setShowClubDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Club' : 'Crear Club'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <Label>Nombre del Club *</Label>
              <Input value={clubForm.nombre} onChange={(e) => setClubForm({ ...clubForm, nombre: e.target.value })} className="bg-slate-700 border-slate-600" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Disciplina Deportiva *</Label>
                <Select value={clubForm.disciplina} onValueChange={(v) => setClubForm({ ...clubForm, disciplina: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {disciplinas.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>País *</Label>
                <Select value={clubForm.pais} onValueChange={(v) => setClubForm({ ...clubForm, pais: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAISES_CONFIG.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">Documento: {getNombreDocumento(clubForm.pais)}</p>
              </div>
            </div>
            <div>
              <Label>Logo del Club</Label>
              <div className="flex items-center gap-4">
                {clubForm.logo && <img src={clubForm.logo} alt="Logo" className="w-16 h-16 rounded-lg object-cover" />}
                <label className="flex items-center gap-2 px-4 py-2 bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-600">
                  <Upload className="w-4 h-4" />
                  <span>Subir imagen</span>
                  <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input value={clubForm.telefono} onChange={(e) => setClubForm({ ...clubForm, telefono: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={clubForm.email} onChange={(e) => setClubForm({ ...clubForm, email: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input value={clubForm.direccion} onChange={(e) => setClubForm({ ...clubForm, direccion: e.target.value })} className="bg-slate-700 border-slate-600" />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea value={clubForm.descripcion} onChange={(e) => setClubForm({ ...clubForm, descripcion: e.target.value })} className="bg-slate-700 border-slate-600" rows={3} />
            </div>
            <div>
              <Label>Texto de Bienvenida (Formulario de Inscripción)</Label>
              <p className="text-xs text-slate-400 mb-2">Este texto aparecerá en el formulario público de inscripción. Puedes incluir bienvenidas, condiciones, normas, etc.</p>
              <Textarea 
                value={clubForm.texto_bienvenida} 
                onChange={(e) => setClubForm({ ...clubForm, texto_bienvenida: e.target.value })} 
                className="bg-slate-700 border-slate-600" 
                rows={4} 
                placeholder="Ej: ¡Bienvenido a nuestro club! Al inscribirte aceptas nuestras normas de convivencia..."
              />
            </div>
            <Button onClick={handleSaveClub} className="w-full bg-blue-600 hover:bg-blue-700">
              {editingItem ? 'Guardar Cambios' : 'Crear Club'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Categoría */}
      <Dialog open={showCategoriaDialog} onOpenChange={setShowCategoriaDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input value={categoriaForm.nombre} onChange={(e) => setCategoriaForm({ ...categoriaForm, nombre: e.target.value })} className="bg-slate-700 border-slate-600" />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea value={categoriaForm.descripcion} onChange={(e) => setCategoriaForm({ ...categoriaForm, descripcion: e.target.value })} className="bg-slate-700 border-slate-600" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Edad Mínima</Label>
                <Input type="number" value={categoriaForm.edad_minima} onChange={(e) => setCategoriaForm({ ...categoriaForm, edad_minima: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Edad Máxima</Label>
                <Input type="number" value={categoriaForm.edad_maxima} onChange={(e) => setCategoriaForm({ ...categoriaForm, edad_maxima: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
            </div>
            <Button onClick={handleSaveCategoria} className="w-full bg-blue-600 hover:bg-blue-700">
              {editingItem ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Integrante */}
      <Dialog open={showIntegranteDialog} onOpenChange={setShowIntegranteDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Integrante' : 'Nuevo Integrante'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={integranteForm.nombre} onChange={(e) => setIntegranteForm({ ...integranteForm, nombre: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Apellido *</Label>
                <Input value={integranteForm.apellido} onChange={(e) => setIntegranteForm({ ...integranteForm, apellido: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Tipo Documento *</Label>
                <Select value={integranteForm.tipo_documento} onValueChange={(v) => setIntegranteForm({ ...integranteForm, tipo_documento: v, rut: '' })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="documento">{getNombreDocumento(clubActual?.pais || 'CL')}</SelectItem>
                    <SelectItem value="pasaporte">Pasaporte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>{integranteForm.tipo_documento === 'pasaporte' ? 'Pasaporte' : getNombreDocumento(clubActual?.pais || 'CL')} *</Label>
                <Input 
                  value={integranteForm.rut} 
                  onChange={(e) => setIntegranteForm({ 
                    ...integranteForm, 
                    rut: integranteForm.tipo_documento === 'documento' 
                      ? formatDocumento(e.target.value, clubActual?.pais || 'CL') 
                      : e.target.value.toUpperCase() 
                  })} 
                  className={`bg-slate-700 border-slate-600 ${integranteForm.rut && !validarDocumento(integranteForm.rut, clubActual?.pais || 'CL', integranteForm.tipo_documento).valido ? 'border-red-500' : ''}`}
                  placeholder={integranteForm.tipo_documento === 'pasaporte' ? 'AB123456' : '12.345.678-9'} 
                  maxLength={integranteForm.tipo_documento === 'pasaporte' ? 20 : 15} 
                />
                {integranteForm.rut && (
                  <p className={`text-xs mt-1 ${validarDocumento(integranteForm.rut, clubActual?.pais || 'CL', integranteForm.tipo_documento).valido ? 'text-green-400' : 'text-red-400'}`}>
                    {validarDocumento(integranteForm.rut, clubActual?.pais || 'CL', integranteForm.tipo_documento).mensaje}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha Nacimiento</Label>
                {(() => {
                  const rutNorm = integranteForm.rut?.replace(/\./g, '').replace(/-/g, '').trim();
                  const enroll = enrollmentData[rutNorm];
                  const isLocked = enroll?.fecha_nacimiento;
                  return (
                    <>
                      <Input type="date"
                        value={isLocked ? enroll.fecha_nacimiento : integranteForm.fecha_nacimiento}
                        onChange={(e) => !isLocked && setIntegranteForm({ ...integranteForm, fecha_nacimiento: e.target.value })}
                        className={`bg-slate-700 border-slate-600 ${isLocked ? 'opacity-70 cursor-not-allowed' : ''}`}
                        readOnly={!!isLocked} />
                      {isLocked && (
                        <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Fecha del enrolamiento
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <Label>Fecha Ingreso</Label>
                <Input type="date" value={integranteForm.fecha_ingreso} onChange={(e) => setIntegranteForm({ ...integranteForm, fecha_ingreso: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={integranteForm.email} onChange={(e) => setIntegranteForm({ ...integranteForm, email: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={integranteForm.telefono} onChange={(e) => setIntegranteForm({ ...integranteForm, telefono: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input value={integranteForm.direccion} onChange={(e) => setIntegranteForm({ ...integranteForm, direccion: e.target.value })} className="bg-slate-700 border-slate-600" />
            </div>
            <div>
              <Label>Categorías</Label>
              <div className="space-y-2 p-3 bg-slate-700/50 rounded-lg border border-slate-600 max-h-48 overflow-y-auto">
                {categorias.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-2">No hay categorías creadas. Crea una categoría primero.</p>
                ) : (
                  categorias.map(cat => {
                    const isSelected = integranteForm.categoria_ids?.includes(cat.id);
                    return (
                      <label
                        key={cat.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                          isSelected 
                            ? 'bg-blue-600/30 border-2 border-blue-500' 
                            : 'bg-slate-700 border-2 border-transparent hover:border-slate-500'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const ids = integranteForm.categoria_ids || [];
                            if (isSelected) {
                              setIntegranteForm({ ...integranteForm, categoria_ids: ids.filter(id => id !== cat.id) });
                            } else {
                              setIntegranteForm({ ...integranteForm, categoria_ids: [...ids, cat.id] });
                            }
                          }}
                          className="w-5 h-5 rounded border-slate-500 text-blue-600 focus:ring-blue-500 focus:ring-offset-slate-800"
                        />
                        <div className="flex-1">
                          <span className="font-medium text-white">{cat.nombre}</span>
                          {(cat.edad_minima || cat.edad_maxima) && (
                            <span className="text-xs text-slate-400 ml-2">
                              ({cat.edad_minima || 0} - {cat.edad_maxima || 99} años)
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Seleccionada</span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              {integranteForm.categoria_ids?.length > 0 && (
                <p className="text-xs text-blue-400 mt-2">
                  {integranteForm.categoria_ids.length} categoría(s) seleccionada(s)
                </p>
              )}
            </div>
            <div>
              <Label>Observaciones</Label>
              <Textarea value={integranteForm.observaciones} onChange={(e) => setIntegranteForm({ ...integranteForm, observaciones: e.target.value })} className="bg-slate-700 border-slate-600" rows={2} />
            </div>
            <Button onClick={handleSaveIntegrante} className="w-full bg-blue-600 hover:bg-blue-700">
              {editingItem ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Invitar Usuario */}
      <Dialog open={showInvitarDialog} onOpenChange={setShowInvitarDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Invitar Usuario al Club</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email del Usuario *</Label>
              <Input type="email" value={invitarForm.email} onChange={(e) => setInvitarForm({ ...invitarForm, email: e.target.value })} className="bg-slate-700 border-slate-600" placeholder="usuario@email.com" />
            </div>
            <div>
              <Label>Rol *</Label>
              <Select value={invitarForm.rol} onValueChange={(v) => setInvitarForm({ ...invitarForm, rol: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_usuario">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-400" />
                      <span>Super Usuario</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="lectura">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-slate-400" />
                      <span>Solo Lectura</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-2">
                {invitarForm.rol === 'super_usuario' 
                  ? 'Puede ver y editar toda la información del club' 
                  : 'Solo puede visualizar la información, sin modificar'}
              </p>
            </div>
            <Button onClick={handleInvitarUsuario} className="w-full bg-blue-600 hover:bg-blue-700">
              <UserPlus className="w-4 h-4 mr-2" /> Enviar Invitación
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Asociar Integrantes a Categoría */}
      <Dialog open={showAsociarDialog} onOpenChange={setShowAsociarDialog}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Agregar Integrantes a "{categoriaSeleccionada?.nombre}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Selecciona los integrantes que deseas agregar a esta categoría.
            </p>
            
            <div className="max-h-96 overflow-y-auto space-y-2">
              {getIntegrantesSinCategoria(categoriaSeleccionada?.id).length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">Todos los integrantes ya están en esta categoría</p>
                </div>
              ) : (
                getIntegrantesSinCategoria(categoriaSeleccionada?.id).map(int => (
                  <div
                    key={int.id}
                    className="flex items-center justify-between p-3 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-600/30 flex items-center justify-center text-blue-400 font-bold">
                        {int.nombre?.charAt(0)}{int.apellido?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-white">{int.apellido}, {int.nombre}</p>
                        <p className="text-xs text-slate-400">{int.rut || 'Sin RUT'} • {int.email || 'Sin email'}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleToggleIntegranteCategoria(int, categoriaSeleccionada?.id)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                ))
              )}
            </div>
            
            <div className="pt-4 border-t border-slate-700 flex justify-end">
              <Button variant="outline" onClick={() => setShowAsociarDialog(false)} className="border-slate-600 text-slate-300">
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Exportar Excel - Selección de campos y orden */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-emerald-400" />
              Exportar a Excel
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Selecciona los campos y el orden de las columnas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {exportFields.map((field, index) => (
              <div key={field.key} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-700/50" data-testid={`export-field-${field.key}`}>
                <input type="checkbox" checked={field.checked}
                  onChange={(e) => {
                    const nf = [...exportFields];
                    nf[index].checked = e.target.checked;
                    setExportFields(nf);
                  }}
                  className="w-4 h-4 rounded border-slate-500 accent-emerald-500" />
                <span className="flex-1 text-sm text-slate-200">{field.label}</span>
                <button onClick={() => moveExportField(index, -1)} disabled={index === 0}
                  className="p-1 text-slate-400 hover:text-white disabled:opacity-30">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => moveExportField(index, 1)} disabled={index === exportFields.length - 1}
                  className="p-1 text-slate-400 hover:text-white disabled:opacity-30">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowExportDialog(false)} className="flex-1 border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleExportarExcel} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="confirm-export-btn">
              <Download className="w-4 h-4" /> Exportar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Importar desde Excel */}
      <Dialog open={showImportarDialog} onOpenChange={setShowImportarDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Importar Integrantes desde Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
              <h4 className="font-medium text-white mb-2">Formato del archivo</h4>
              <p className="text-sm text-slate-400 mb-3">
                El archivo Excel debe contener las siguientes columnas:
              </p>
              <ul className="text-sm text-slate-300 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <strong>RUT</strong> (obligatorio) - Identificador único
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <strong>Nombre</strong> (obligatorio)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <strong>Apellido</strong> (obligatorio)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-500 rounded-full"></span>
                  <strong>Email</strong> (opcional)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-500 rounded-full"></span>
                  <strong>Teléfono</strong> (opcional)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-500 rounded-full"></span>
                  <strong>Fecha Nacimiento</strong> (opcional)
                </li>
              </ul>
            </div>
            
            <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-700">
              <h4 className="font-medium text-blue-300 mb-2">Comportamiento de importación</h4>
              <ul className="text-sm text-blue-200 space-y-1">
                <li>• Si el RUT ya existe: se asocia a la categoría "{categoriaSeleccionada?.nombre}"</li>
                <li>• Si el RUT no existe: se crea el integrante y se asocia</li>
              </ul>
            </div>

            <div className="pt-4">
              <Label>Seleccionar archivo Excel</Label>
              <div className="mt-2">
                <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all ${importandoExcel ? 'border-blue-500 bg-blue-600/20' : 'border-slate-600 hover:border-blue-500 hover:bg-slate-700/50'}`}>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {importandoExcel ? (
                      <>
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2" />
                        <p className="text-sm text-blue-400">Importando...</p>
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-10 h-10 text-slate-400 mb-2" />
                        <p className="text-sm text-slate-400">
                          <span className="font-semibold text-blue-400">Click para seleccionar</span> archivo .xlsx
                        </p>
                      </>
                    )}
                  </div>
                  <input 
                    type="file" 
                    accept=".xlsx,.xls" 
                    onChange={handleImportarExcel} 
                    className="hidden"
                    disabled={importandoExcel}
                  />
                </label>
              </div>
            </div>
            
            <div className="pt-4 border-t border-slate-700 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowImportarDialog(false)} className="border-slate-600 text-slate-300" disabled={importandoExcel}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Link de Inscripción */}
      <Dialog open={showLinkInscripcionDialog} onOpenChange={setShowLinkInscripcionDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Link de Inscripción</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Comparte este link para que las personas se inscriban a la categoría <strong className="text-blue-400">{categoriaSeleccionada?.nombre}</strong>. Las solicitudes llegarán a la sección "Pendientes" para tu aprobación.
            </p>
            
            <div className="p-3 bg-slate-700 rounded-lg flex items-center gap-2">
              <Input 
                value={linkInscripcion || ''} 
                readOnly 
                className="bg-transparent border-0 text-slate-200 text-sm flex-1"
              />
              <Button 
                size="sm" 
                onClick={() => handleCopyLink(linkInscripcion)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex gap-2 pt-4 border-t border-slate-700">
              <Button 
                onClick={() => window.open(linkInscripcion, '_blank')}
                variant="outline" 
                className="flex-1 border-slate-600 text-slate-300"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Abrir Link
              </Button>
              <Button 
                onClick={() => setShowLinkInscripcionDialog(false)}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Ver Cartola del Integrante */}
      <Dialog open={showCartolaDialog} onOpenChange={setShowCartolaDialog}>
        <DialogContent className="max-w-3xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-green-400" />
              Cartola de {cartolaIntegrante?.nombre} {cartolaIntegrante?.apellido}
            </DialogTitle>
          </DialogHeader>
          
          {loadingCartola ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : cartolaData ? (
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              {/* Resumen */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-red-500/20 rounded-lg border border-red-500/30">
                  <p className="text-xs text-red-400">Deuda Total</p>
                  <p className="text-lg font-bold text-white">{formatMoney(cartolaData.resumen?.total_deuda || 0)}</p>
                </div>
                <div className="p-3 bg-orange-500/20 rounded-lg border border-orange-500/30">
                  <p className="text-xs text-orange-400">Deuda Vencida</p>
                  <p className="text-lg font-bold text-white">{formatMoney(cartolaData.resumen?.deuda_vencida || 0)}</p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-lg border border-green-500/30">
                  <p className="text-xs text-green-400">Total Pagado</p>
                  <p className="text-lg font-bold text-white">{formatMoney(cartolaData.resumen?.total_pagado || 0)}</p>
                </div>
              </div>

              {/* Botón de Pagar por Transferencia */}
              {cartolaData.resumen?.total_deuda > 0 && datosBancarios && (
                <div className="bg-green-900/20 border border-green-700/40 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-300 text-sm font-medium">¿Pagar por transferencia?</p>
                      <p className="text-green-400/70 text-xs">Genera un código único para tu transferencia bancaria</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        // Seleccionar cuotas pendientes
                        const cuotasPendientes = cartolaData.cuotas?.filter(c => c.estado !== 'pagada') || [];
                        if (cuotasPendientes.length > 0) {
                          handleCrearTransaccionPago(cartolaIntegrante.id, cuotasPendientes.map(c => c.id));
                        }
                      }}
                      disabled={loadingTransaccion}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="btn-pagar-transferencia"
                    >
                      {loadingTransaccion ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Wallet className="w-4 h-4 mr-2" />
                      )}
                      Generar Código
                    </Button>
                  </div>
                </div>
              )}

              {/* Tabs de navegación */}
              <div className="flex gap-2 border-b border-slate-700 pb-2">
                <button
                  onClick={() => setCartolaTab('cuotas')}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${cartolaTab === 'cuotas' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                >
                  Cuotas
                </button>
                <button
                  onClick={() => setCartolaTab('pagos')}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${cartolaTab === 'pagos' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                >
                  Historial Pagos ({cartolaPagos.length})
                </button>
                <button
                  onClick={() => setCartolaTab('planes')}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${cartolaTab === 'planes' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                >
                  Planes ({cartolaPlanes.length})
                </button>
              </div>

              {/* Tab: Cuotas */}
              {cartolaTab === 'cuotas' && (
                <div>
                  {/* Filtro por plan */}
                  <div className="mb-3">
                    <Select value={cartolaFiltro} onValueChange={setCartolaFiltro}>
                      <SelectTrigger className="w-full bg-slate-700 border-slate-600 text-sm">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Filtrar por tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todo</SelectItem>
                        {cartolaPlanes.map(p => (
                          <SelectItem key={p.plan.id} value={p.plan.id}>
                            {p.plan.nombre}
                          </SelectItem>
                        ))}
                        <SelectItem value="otros">Otros (Cargos excepcionales)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {cuotasFiltradas.length > 0 ? (
                    <div className="space-y-2 max-h-52 overflow-y-auto">
                      {cuotasFiltradas.map((cuota, idx) => (
                        <div key={cuota.id || idx} className="p-3 bg-slate-700 rounded-lg flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">{cuota.descripcion}</p>
                            <p className="text-xs text-slate-400">Vence: {cuota.fecha_vencimiento}
                              {cuota.motivo_descuento && <span className="text-green-400 ml-2">• {cuota.motivo_descuento}</span>}
                            </p>
                            {cuota.pagos?.length > 0 && (
                              <p className="text-xs text-green-400 mt-1">
                                {cuota.pagos.length} pago(s) registrado(s)
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">{formatMoney(cuota.monto_final)}</p>
                            {cuota.monto_pagado > 0 && (
                              <p className="text-xs text-green-400">Pagado: {formatMoney(cuota.monto_pagado)}</p>
                            )}
                            <Badge className={
                              cuota.estado === 'pagada' ? 'bg-green-500/20 text-green-400' :
                              cuota.estado === 'vencida' ? 'bg-red-500/20 text-red-400' :
                              cuota.estado === 'parcial' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }>
                              {cuota.estado === 'pagada' ? 'Pagada' : 
                               cuota.estado === 'vencida' ? 'Vencida' : 
                               cuota.estado === 'parcial' ? 'Parcial' : 'Pendiente'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm text-center py-4">No hay cuotas para mostrar</p>
                  )}

                  {/* Cargos Excepcionales */}
                  {cartolaData.cargos_excepcionales?.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Cargos Excepcionales</h4>
                      <div className="space-y-2">
                        {cartolaData.cargos_excepcionales.map((cargo, idx) => (
                          <div key={cargo.id || idx} className="p-3 bg-slate-700 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm">{cargo.descripcion}</p>
                              <p className="text-xs text-slate-400">Vence: {cargo.fecha_vencimiento}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-medium">{formatMoney(cargo.monto)}</p>
                              <Badge className={cargo.estado === 'pagado' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                {cargo.estado === 'pagado' ? 'Pagado' : 'Pendiente'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Excepciones */}
                  {cartolaData.excepciones?.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-slate-300">Excepciones Activas</h4>
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={handleRecalcularCuotas} className="text-xs border-blue-500 text-blue-400 hover:bg-blue-500/20">
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Recalcular Cuotas
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {cartolaData.excepciones.map((exc, idx) => (
                          <div key={exc.id || idx} className="p-3 bg-blue-500/20 rounded-lg border border-blue-500/30 flex items-center justify-between">
                            <div>
                              <p className="text-blue-300 text-sm">
                                {exc.tipo === 'descuento_permanente' ? `Descuento ${exc.valor}%` :
                                 exc.tipo === 'descuento_fijo' ? `Descuento Fijo $${exc.valor?.toLocaleString('es-CL')}` :
                                 exc.tipo === 'exencion_total' ? 'Exención Total' :
                                 `Exención Temporal: ${exc.meses_exencion?.join(', ')}`}
                              </p>
                              {exc.motivo && <p className="text-xs text-slate-400 mt-1">{exc.motivo}</p>}
                            </div>
                            {canEdit && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDesactivarExcepcion(exc.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                title="Desactivar excepción"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Historial de Pagos */}
              {cartolaTab === 'pagos' && (
                <div>
                  {cartolaPagos.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {cartolaPagos.map((pago, idx) => (
                        <div key={pago.id || idx} className="p-3 bg-slate-700 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{pago.glosa || 'Pago registrado'}</p>
                              <p className="text-xs text-slate-400">
                                {pago.fecha_pago} • {pago.metodo_pago}
                                {pago.comprobante && ` • #${pago.comprobante}`}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                Aplicado a {pago.cuota_ids?.length || 0} cuota(s)
                              </p>
                            </div>
                            <div className="text-right flex items-center gap-3">
                              <p className="text-xl font-bold text-green-400">{formatMoney(pago.monto)}</p>
                              {canEdit && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleAnularPago(pago.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                  title="Anular pago"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm text-center py-8">No hay pagos registrados</p>
                  )}
                </div>
              )}

              {/* Tab: Planes Asignados */}
              {cartolaTab === 'planes' && (
                <div>
                  {cartolaPlanes.length > 0 ? (
                    <div className="space-y-3">
                      {cartolaPlanes.map((item, idx) => (
                        <div key={item.plan?.id || idx} className="p-4 bg-slate-700 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="text-white font-semibold">{item.plan?.nombre}</h4>
                              <p className="text-sm text-slate-400">
                                {formatMoney(item.plan?.monto)} / {item.plan?.frecuencia}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                Asignado: {item.fecha_asignacion}
                              </p>
                            </div>
                            {canEdit && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDesasignarPlan(item.plan?.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                title="Desasignar plan"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-3">
                            <div className="p-2 bg-slate-600/50 rounded text-center">
                              <p className="text-xs text-slate-400">Total Cuotas</p>
                              <p className="text-white font-bold">{item.total_cuotas}</p>
                            </div>
                            <div className="p-2 bg-green-500/20 rounded text-center">
                              <p className="text-xs text-green-400">Pagadas</p>
                              <p className="text-white font-bold">{item.cuotas_pagadas}</p>
                            </div>
                            <div className="p-2 bg-red-500/20 rounded text-center">
                              <p className="text-xs text-red-400">Deuda</p>
                              <p className="text-white font-bold">{formatMoney(item.deuda_pendiente)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm text-center py-8">No hay planes asignados</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center">
              <Receipt className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">No hay información financiera disponible</p>
            </div>
          )}

          <div className="pt-4 border-t border-slate-700 mt-auto">
            <Button onClick={() => { setShowCartolaDialog(false); setCartolaData(null); setCartolaIntegrante(null); setCartolaPlanes([]); setCartolaPagos([]); }} className="w-full">
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Aceptación de Términos */}
      <TermsAcceptanceModal 
        open={showTermsModal} 
        onAccepted={handleTermsAccepted}
        userName={user?.name}
      />

      {/* Modal para copiar link manualmente */}
      <Dialog open={!!linkParaCopiar} onOpenChange={() => setLinkParaCopiar(null)}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-400" />
              Copiar Link
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400 mb-4">
            No pudimos copiar automáticamente. Selecciona el link y cópialo manualmente:
          </p>
          <div className="bg-slate-900 p-3 rounded-lg border border-slate-600">
            <input
              type="text"
              readOnly
              value={linkParaCopiar || ''}
              className="w-full bg-transparent text-blue-300 text-sm font-mono outline-none select-all"
              onClick={(e) => e.target.select()}
              data-testid="link-para-copiar-input"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={async () => {
                const success = await copyToClipboard(linkParaCopiar);
                if (success) {
                  toast.success('Link copiado');
                  setLinkParaCopiar(null);
                } else {
                  toast.info('Selecciona el texto y presiona Ctrl+C');
                }
              }}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              <Copy className="w-4 h-4 mr-2" /> Intentar copiar
            </Button>
            <Button onClick={() => setLinkParaCopiar(null)} variant="outline" className="flex-1">
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Datos Bancarios */}
      <Dialog open={showDatosBancariosDialog} onOpenChange={setShowDatosBancariosDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-400" />
              Configurar Datos Bancarios
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400 mb-4">
            Estos datos se mostrarán a los integrantes cuando quieran pagar por transferencia.
          </p>
          <div className="space-y-4">
            <div>
              <Label>Banco *</Label>
              <Select value={datosBancariosForm.banco} onValueChange={(v) => setDatosBancariosForm({...datosBancariosForm, banco: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Seleccionar banco" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600 max-h-[300px]">
                  <SelectItem value="Banco de Chile / Edwards / Credichile" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco de Chile / Edwards / Credichile</SelectItem>
                  <SelectItem value="Banco Estado" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Estado</SelectItem>
                  <SelectItem value="Banco Scotiabank" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Scotiabank</SelectItem>
                  <SelectItem value="Banco Bci / Mach" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Bci / Mach</SelectItem>
                  <SelectItem value="Banco CORP Banca" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco CORP Banca</SelectItem>
                  <SelectItem value="Banco Bice" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Bice</SelectItem>
                  <SelectItem value="Banco Santander / Banefe" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Santander / Banefe</SelectItem>
                  <SelectItem value="Banco ITAU" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco ITAU</SelectItem>
                  <SelectItem value="Banco Security" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Security</SelectItem>
                  <SelectItem value="Banco Falabella" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Falabella</SelectItem>
                  <SelectItem value="Banco BBVA" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco BBVA</SelectItem>
                  <SelectItem value="Banco del Desarrollo" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco del Desarrollo</SelectItem>
                  <SelectItem value="HSBC Bank" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">HSBC Bank</SelectItem>
                  <SelectItem value="Banco Internacional" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Internacional</SelectItem>
                  <SelectItem value="Banco Ripley" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Ripley</SelectItem>
                  <SelectItem value="Banco Consorcio" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Consorcio</SelectItem>
                  <SelectItem value="Banco Paris" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Banco Paris</SelectItem>
                  <SelectItem value="Tanner" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Tanner</SelectItem>
                  <SelectItem value="Coopeuch / Dale" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Coopeuch / Dale</SelectItem>
                  <SelectItem value="Prepago Los Heroes" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Prepago Los Heroes</SelectItem>
                  <SelectItem value="TENPO Prepago" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">TENPO Prepago</SelectItem>
                  <SelectItem value="Mercado Pago" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Mercado Pago</SelectItem>
                  <SelectItem value="TAPP Caja los Andes" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">TAPP Caja los Andes</SelectItem>
                  <SelectItem value="La Polar Prepago" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">La Polar Prepago</SelectItem>
                  <SelectItem value="Copec Pay" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Copec Pay</SelectItem>
                  <SelectItem value="Global66" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Global66</SelectItem>
                  <SelectItem value="Prex" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Prex</SelectItem>
                  <SelectItem value="Fintual" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Fintual</SelectItem>
                  <SelectItem value="JP Morgan" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">JP Morgan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Cuenta *</Label>
                <Select value={datosBancariosForm.tipo_cuenta} onValueChange={(v) => setDatosBancariosForm({...datosBancariosForm, tipo_cuenta: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="corriente" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Cuenta Corriente</SelectItem>
                    <SelectItem value="vista" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Cuenta Vista / RUT</SelectItem>
                    <SelectItem value="ahorro" className="text-white hover:bg-slate-600 focus:bg-slate-600 focus:text-white">Cuenta de Ahorro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Número de Cuenta *</Label>
                <Input 
                  value={datosBancariosForm.numero_cuenta} 
                  onChange={(e) => setDatosBancariosForm({...datosBancariosForm, numero_cuenta: e.target.value})} 
                  className="bg-slate-700 border-slate-600"
                  placeholder="Ej: 12345678"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>RUT del Titular *</Label>
                <Input 
                  value={datosBancariosForm.rut_titular} 
                  onChange={(e) => setDatosBancariosForm({...datosBancariosForm, rut_titular: formatRut(e.target.value)})} 
                  className="bg-slate-700 border-slate-600"
                  placeholder="12.345.678-9"
                />
              </div>
              <div>
                <Label>Nombre del Titular *</Label>
                <Input 
                  value={datosBancariosForm.nombre_titular} 
                  onChange={(e) => setDatosBancariosForm({...datosBancariosForm, nombre_titular: e.target.value})} 
                  className="bg-slate-700 border-slate-600"
                  placeholder="Juan Pérez"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => setShowDatosBancariosDialog(false)} variant="outline" className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveDatosBancarios} 
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={!datosBancariosForm.banco || !datosBancariosForm.numero_cuenta || !datosBancariosForm.rut_titular || !datosBancariosForm.nombre_titular}
              data-testid="guardar-datos-bancarios-btn"
            >
              Guardar Datos
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Transacción de Pago */}
      <Dialog open={showPagoTransferenciaDialog} onOpenChange={setShowPagoTransferenciaDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-green-400" />
              Instrucciones de Pago
            </DialogTitle>
          </DialogHeader>
          {transaccionActiva && (
            <div className="space-y-4">
              <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-4 text-center">
                <p className="text-sm text-green-300 mb-2">Tu código de transacción es:</p>
                <p className="text-3xl font-bold text-green-400 font-mono">{transaccionActiva.transaccion?.codigo}</p>
                <p className="text-xs text-green-300/70 mt-2">
                  Incluye este código en la glosa de tu transferencia
                </p>
              </div>
              
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-white mb-3">Datos para la transferencia:</p>
                {transaccionActiva.datos_bancarios ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Banco:</span>
                      <span className="text-white">{transaccionActiva.datos_bancarios.banco}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Tipo Cuenta:</span>
                      <span className="text-white capitalize">{transaccionActiva.datos_bancarios.tipo_cuenta}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">N° Cuenta:</span>
                      <span className="text-white font-mono">{transaccionActiva.datos_bancarios.numero_cuenta}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">RUT:</span>
                      <span className="text-white">{transaccionActiva.datos_bancarios.rut_titular}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Titular:</span>
                      <span className="text-white">{transaccionActiva.datos_bancarios.nombre_titular}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-amber-400 text-sm">El club no ha configurado datos bancarios</p>
                )}
              </div>
              
              <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-blue-300">Monto a pagar:</span>
                  <span className="text-2xl font-bold text-white">${transaccionActiva.transaccion?.monto?.toLocaleString('es-CL')}</span>
                </div>
                <p className="text-xs text-blue-300/70 mt-2">
                  Código válido hasta: {transaccionActiva.transaccion?.expira}
                </p>
              </div>
              
              <div className="bg-slate-700/30 rounded-lg p-3">
                <p className="text-xs text-slate-400 leading-relaxed">
                  <strong className="text-white">Importante:</strong> Incluye el código <strong className="text-green-400">{transaccionActiva.transaccion?.codigo}</strong> en el mensaje o glosa de tu transferencia. Tu pago será confirmado automáticamente cuando recibamos la notificación del banco.
                </p>
              </div>
            </div>
          )}
          <Button onClick={() => setShowPagoTransferenciaDialog(false)} className="w-full mt-4">
            Entendido
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Modal Upgrade a Pro */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              Actualizar a Plan Pro
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-700">
              <p className="text-blue-300 font-medium">Plan Pro - $6 USD/mes</p>
              <ul className="text-sm text-slate-400 mt-2 space-y-1">
                <li>✓ Categorías ilimitadas</li>
                <li>✓ Integrantes ilimitados</li>
                <li>✓ Módulo de Contabilidad completo</li>
                <li>✓ Pago automático por transferencia</li>
                <li>✓ Reportes financieros</li>
                <li>✓ Soporte prioritario</li>
              </ul>
            </div>
            
            <div>
              <Label className="text-slate-300">Nombre *</Label>
              <Input
                value={upgradeForm.nombre}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, nombre: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={upgradeForm.email}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, email: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300">Teléfono</Label>
              <Input
                value={upgradeForm.telefono}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, telefono: e.target.value })}
                placeholder="+56 9 XXXX XXXX"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300">Mensaje (opcional)</Label>
              <Textarea
                value={upgradeForm.mensaje}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, mensaje: e.target.value })}
                placeholder="¿Tienes alguna consulta adicional?"
                className="bg-slate-700 border-slate-600"
                rows={3}
              />
            </div>
            
            <p className="text-xs text-slate-500">
              Club: <span className="text-slate-400">{clubActual?.nombre}</span>
            </p>
            
            <Button 
              onClick={handleEnviarUpgrade} 
              disabled={enviandoUpgrade}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500"
            >
              {enviandoUpgrade ? (
                <>Enviando...</>
              ) : (
                <>Solicitar Upgrade</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Suspender Integrante */}
      <Dialog open={showSuspenderDialog} onOpenChange={setShowSuspenderDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Suspender Integrante</DialogTitle>
          </DialogHeader>
          {integranteASuspender && (
            <div className="space-y-4">
              <p className="text-slate-300">
                ¿Suspender a <span className="font-semibold text-white">{integranteASuspender.nombre} {integranteASuspender.apellido}</span>?
              </p>
              <p className="text-sm text-slate-400">
                Sus cuotas pendientes no se contarán en la deuda total mientras esté suspendido.
              </p>
              
              <div>
                <Label className="text-slate-300">Motivo de suspensión *</Label>
                <Select value={suspenderForm.motivo} onValueChange={(v) => setSuspenderForm(prev => ({ ...prev, motivo: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Selecciona un motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retiro_voluntario">Retiro voluntario</SelectItem>
                    <SelectItem value="deuda">Por deuda pendiente</SelectItem>
                    <SelectItem value="inasistencia">Por inasistencia</SelectItem>
                    <SelectItem value="otro">Otro motivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-slate-300">Detalle (opcional)</Label>
                <Input
                  value={suspenderForm.detalle}
                  onChange={(e) => setSuspenderForm(prev => ({ ...prev, detalle: e.target.value }))}
                  placeholder="Información adicional..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowSuspenderDialog(false); setSuspenderForm({ motivo: '', detalle: '' }); }} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleSuspenderIntegrante} className="flex-1 bg-amber-600 hover:bg-amber-700" disabled={!suspenderForm.motivo}>
                  Suspender
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
